import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashLayout from "@/components/app/DashLayout";
import { LayoutDashboard, Landmark, Users2, Building2, Upload, ListFilter, ScrollText, Plus, Trash2, IndianRupee, TrendingUp, ShieldOff, Ban, Pause, XOctagon, ShieldCheck, ScrollText as AuditIcon, BarChart3, Crown, Download, RefreshCw, CheckCircle2, XCircle, Wallet, Target, Settings as SettingsIcon, Gift, MapPin, Timer, UserPlus, Snowflake, History as HistoryIcon, Clock, Pencil, Save, X, GripVertical, KeyRound, Phone as PhoneIcon, FolderSync, Handshake, Map, AlertTriangle, Eye } from "lucide-react";
import { LOAN_ICON_MAP, LoanIcon } from "@/lib/loanIcons";
import { Textarea } from "@/components/ui/textarea";
import { BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api, { errorMessage } from "@/lib/api";
import { useRefocus } from "@/lib/refocus";
import LoadingSkeleton from "@/components/app/LoadingSkeleton";
import Paginator from "@/components/app/Paginator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import PipelineTimeline from "@/components/app/PipelineTimeline";
import RejectionHistory from "@/components/app/RejectionHistory";
import SettingsPage from "@/pages/admin/SettingsPage";
import PayoutPage from "@/pages/admin/PayoutPage";
import ReferralLeaderboardCard from "@/pages/admin/ReferralLeaderboardCard";
import LocationMasterPage from "@/pages/admin/LocationMasterPage";
import AdminPartnersPage from "@/pages/admin/AdminPartnersPage";
import AdminMasterMap from "@/pages/admin/AdminMasterMap";
import SLAMonitorPage from "@/pages/admin/SLAMonitorPage";
import UploadLeadsPage from "@/pages/admin/UploadLeadsPage";
import SourceReportPage from "@/pages/admin/SourceReportPage";
import SourcesPoolPage from "@/pages/admin/SourcesPoolPage";
import UniversalLeadAdd from "@/components/app/UniversalLeadAdd";
import AdminMovingFiles from "@/pages/admin/AdminMovingFiles";
import ImpersonationLogsPage from "@/pages/admin/ImpersonationLogsPage";
import AdminEditUserModal from "@/components/app/AdminEditUserModal";
import InlineEditCell from "@/components/app/InlineEditCell";
import StorageHealthCard from "@/components/app/StorageHealthCard";
import TelecallerBackfillCard from "@/components/app/TelecallerBackfillCard";
import { beginImpersonation } from "@/lib/impersonation";
import AssignmentHeatmap from "@/components/app/AssignmentHeatmap";
import DailyFuelMeter from "@/components/app/DailyFuelMeter";
import InBankPoolPage from "@/pages/shared/InBankPoolPage";
import CallLogsPage from "@/pages/shared/CallLogsPage";
import useLocations from "@/hooks/useLocations";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const NAV = [
  { key: "all_leads", label: "All Leads", icon: ListFilter },
  { key: "upload", label: "Upload Leads", icon: Upload },
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "quick_add", label: "Quick Add Lead", icon: UserPlus },
  { key: "location", label: "Location Master", icon: MapPin },
  { key: "loans", label: "Loans", icon: ScrollText },
  { key: "banks", label: "Banks", icon: Landmark },
  { key: "in_bank_pool", label: "In Bank Pool", icon: Landmark },
  { key: "moving_files", label: "Moving Files", icon: FolderSync },
  { key: "bank_staff", label: "Bank Staff", icon: Building2 },
  { key: "office_staff", label: "Office Staff", icon: Users2 },
  { key: "partners", label: "Partner Requests", icon: Handshake },
  { key: "master_map", label: "Master Map", icon: Map },
  { key: "call_logs", label: "Call Logs", icon: PhoneIcon },
  { key: "sources_pool", label: "Source Pool (Cold)", icon: Snowflake },
  { key: "commission", label: "Commission Report", icon: BarChart3 },
  { key: "payroll", label: "Payroll", icon: Wallet },
  { key: "payout", label: "Incentive Payout", icon: Gift },
  { key: "refunds", label: "Refunds", icon: RefreshCw },
  { key: "audit", label: "Status Audit Log", icon: AuditIcon },
  { key: "impersonation_logs", label: "Impersonation Logs", icon: Eye },
  { key: "settings", label: "Settings", icon: SettingsIcon },
];

const DISTRICTS = [];

// -- Overview -----------------------------------------------
function Overview() {
  const [stats, setStats] = useState(null);
  const load = () => api.get("/admin/stats").then(r => setStats(r.data)).catch(e => toast.error(errorMessage(e)));
  useEffect(() => { load(); }, []);
  useRefocus(load);
  const loaded = !!stats;
  const s = stats || {};
  const cards = [
    { label: "Total Leads", value: s.total_leads, icon: ListFilter },
    { label: "Disbursed", value: s.disbursed, icon: TrendingUp },
    { label: "Earnings", value: loaded ? `₹${(s.earnings||0).toLocaleString("en-IN")}` : undefined, icon: IndianRupee },
    { label: "Banned Staff", value: s.banned_staff, icon: Ban },
  ];
  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {cards.map((c, i) => (
          <div key={i} className="card-surface p-6" data-testid={`stat-${c.label.toLowerCase().replace(/ /g,'-')}`}>
            <div className="flex items-center justify-between mb-4">
              <c.icon className="w-5 h-5 text-amber-500"/>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">{loaded ? "Live" : "Loading"}</div>
            </div>
            <div className="font-display text-4xl text-white">{loaded ? (c.value ?? "—") : "—"}</div>
            <div className="text-xs uppercase tracking-widest text-slate-500 mt-2">{c.label}</div>
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <StorageHealthCard/>
        <TelecallerBackfillCard/>
      </div>
      <ReferralLeaderboardCard />
      <AssignmentHeatmap />
    </div>
  );
}

// -- Loans -----------------------------------------------
function LoansPage() {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [f, setF] = useState({ name: "", min_amount: "", max_amount: "", interest_rate: "1", icon: "coins" });
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [edit, setEdit] = useState({ min_amount: "", max_amount: "", interest_rate: "", icon: "coins" });
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/loans"); setLoans(r.data || []); setLoaded(true); }
    catch (e) { toast.error(errorMessage(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useRefocus(load);
  const create = async () => {
    try {
      await api.post("/admin/loans", { ...f, min_amount: parseFloat(f.min_amount), max_amount: parseFloat(f.max_amount), interest_rate: parseFloat(f.interest_rate) });
      toast.success("Loan created"); setOpen(false); setF({ name:"", min_amount:"", max_amount:"", interest_rate:"1", icon:"coins"}); load();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const del = async (id) => { await api.delete(`/admin/loans/${id}`); load(); toast.success("Deleted"); };
  const startEdit = (l) => {
    setEditingId(l.id);
    setEdit({
      min_amount: l.min_amount ?? "",
      max_amount: l.max_amount ?? "",
      interest_rate: l.interest_rate ?? l.commission_pct ?? "",
      icon: l.icon || guessIconKey(l.name),
    });
  };
  const cancelEdit = () => { setEditingId(null); };
  const saveEdit = async (id) => {
    try {
      await api.put(`/admin/loans/${id}`, {
        min_amount: parseFloat(edit.min_amount),
        max_amount: parseFloat(edit.max_amount),
        interest_rate: parseFloat(edit.interest_rate || 0),
        icon: edit.icon,
      });
      toast.success("Loan updated");
      setEditingId(null);
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  // Drag-and-drop reorder ---------------------------------------------------
  const onDragStart = (i) => (e) => {
    setDragIdx(i);
    try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", String(i)); } catch {}
  };
  const onDragOver = (i) => (e) => { e.preventDefault(); if (overIdx !== i) setOverIdx(i); };
  const onDragEnd = () => { setDragIdx(null); setOverIdx(null); };
  const onDrop = (i) => async (e) => {
    e.preventDefault();
    const from = dragIdx;
    setDragIdx(null); setOverIdx(null);
    if (from == null || from === i) return;
    const next = loans.slice();
    const [moved] = next.splice(from, 1);
    next.splice(i, 0, moved);
    setLoans(next);
    try {
      await api.post("/admin/loans/reorder", { loan_ids: next.map(l => l.id) });
      toast.success("Order saved");
    } catch (err) { toast.error(errorMessage(err)); load(); }
  };
  return (
    <div className="card-surface p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div><div className="font-heading text-xl text-white">Loans</div><div className="text-sm text-slate-500">Master list · this order flows into every referral modal (Landing, Universal Add, Bank Staff Refer, Partner).</div></div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            data-testid="loans-refresh"
            title="Refresh"
            className="w-9 h-9 rounded-md bg-slate-800 hover:bg-slate-700 border border-slate-700 flex items-center justify-center text-slate-400"
          ><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}/></button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-loan-btn" className="bg-amber-500 hover:bg-amber-400 text-slate-900 rounded-full"><Plus className="w-4 h-4 mr-2"/>Add Loan</Button></DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-800 text-white">
            <DialogHeader><DialogTitle>New Loan</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input data-testid="loan-name" placeholder="Name (e.g. HOME LOAN)" value={f.name} onChange={e=>setF({...f,name:e.target.value})} className="bg-slate-950 border-slate-700"/>
              <Input data-testid="loan-min" placeholder="Min amount" value={f.min_amount} onChange={e=>setF({...f,min_amount:e.target.value.replace(/\D/g,'')})} className="bg-slate-950 border-slate-700"/>
              <Input data-testid="loan-max" placeholder="Max amount" value={f.max_amount} onChange={e=>setF({...f,max_amount:e.target.value.replace(/\D/g,'')})} className="bg-slate-950 border-slate-700"/>
              <div>
                <Label className="text-slate-400 text-xs uppercase tracking-widest">Rate of Interest (%)</Label>
                <Input data-testid="loan-interest-rate" placeholder="e.g. 8.5" value={f.interest_rate} onChange={e=>setF({...f,interest_rate:e.target.value})} className="bg-slate-950 border-slate-700 mt-2"/>
              </div>
              <div>
                <Label className="text-slate-400 text-xs uppercase tracking-widest">Icon</Label>
                <LoanIconPicker value={f.icon} onChange={(v) => setF({...f, icon: v})} testIdPrefix="loan-new"/>
              </div>
              <Button data-testid="loan-save" onClick={create} className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>
      {/* Prominent reorder hint — makes the drag handle discoverable. */}
      <div
        data-testid="loans-reorder-banner"
        className="mb-4 rounded-xl border border-amber-500/40 bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent p-3 flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-amber-500/15 border border-amber-500/40 flex items-center justify-center text-amber-400 shrink-0">
          <GripVertical className="w-5 h-5"/>
        </div>
        <div className="text-xs leading-relaxed">
          <div className="text-amber-300 font-bold uppercase tracking-widest text-[10px] mb-0.5">Drag to reorder</div>
          <div className="text-slate-300">Grab the <span className="inline-block align-middle mx-0.5"><GripVertical className="w-3 h-3 inline text-amber-400"/></span> handle on any row and drop it where you want. This exact order is what telecallers, bank staff, partners and the public landing page see in their loan dropdowns.</div>
        </div>
      </div>
      <Table>
        <TableHeader><TableRow className="border-white/5"><TableHead className="w-10"></TableHead><TableHead className="w-14">Icon</TableHead><TableHead>Name</TableHead><TableHead>Min</TableHead><TableHead>Max</TableHead><TableHead>Rate</TableHead><TableHead className="w-32 text-right">Actions</TableHead></TableRow></TableHeader>
        <TableBody>
          {loans.map((l, i) => {
            const isEditing = editingId === l.id;
            const isDraggingRow = dragIdx === i;
            const isDropTarget = overIdx === i && dragIdx !== null && dragIdx !== i;
            return (
              <TableRow key={l.id}
                data-testid={`loan-row-${l.id}`}
                draggable={!isEditing}
                onDragStart={onDragStart(i)}
                onDragOver={onDragOver(i)}
                onDragEnd={onDragEnd}
                onDrop={onDrop(i)}
                className={`border-white/5 transition-colors ${isDraggingRow ? "opacity-40" : ""} ${isDropTarget ? "bg-amber-500/10 border-t-2 border-t-amber-500" : ""}`}>
                <TableCell className="cursor-grab active:cursor-grabbing text-amber-400/70 hover:text-amber-400 hover:bg-amber-500/10 rounded" data-testid={`loan-drag-${l.id}`} title="Drag to reorder">
                  <GripVertical className="w-5 h-5"/>
                </TableCell>
                <TableCell>
                  {isEditing
                    ? <LoanIconPicker compact value={edit.icon} onChange={(v) => setEdit({...edit, icon: v})} testIdPrefix={`loan-edit-${l.id}`}/>
                    : <div className="w-8 h-8 rounded-md bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400" data-testid={`loan-icon-${l.id}`}><LoanIcon loan={l}/></div>}
                </TableCell>
                <TableCell className="text-white font-bold">{l.name}</TableCell>
                <TableCell>
                  {isEditing
                    ? <Input data-testid={`loan-edit-min-${l.id}`} value={edit.min_amount} onChange={e=>setEdit({...edit, min_amount: e.target.value.replace(/\D/g,'')})} className="bg-slate-950 border-slate-700 w-32 h-8"/>
                    : <span className="font-mono-alt text-slate-300">₹{Number(l.min_amount||0).toLocaleString("en-IN")}</span>}
                </TableCell>
                <TableCell>
                  {isEditing
                    ? <Input data-testid={`loan-edit-max-${l.id}`} value={edit.max_amount} onChange={e=>setEdit({...edit, max_amount: e.target.value.replace(/\D/g,'')})} className="bg-slate-950 border-slate-700 w-32 h-8"/>
                    : <span className="font-mono-alt text-slate-300">₹{Number(l.max_amount||0).toLocaleString("en-IN")}</span>}
                </TableCell>
                <TableCell>
                  {isEditing
                    ? <Input data-testid={`loan-edit-rate-${l.id}`} value={edit.interest_rate} onChange={e=>setEdit({...edit, interest_rate: e.target.value})} className="bg-slate-950 border-slate-700 w-20 h-8"/>
                    : <span className="text-slate-300">{l.interest_rate ?? l.commission_pct ?? 0}%</span>}
                </TableCell>
                <TableCell className="whitespace-nowrap text-right">
                  {isEditing ? (
                    <>
                      <Button size="sm" variant="ghost" onClick={cancelEdit} data-testid={`loan-cancel-${l.id}`} className="text-slate-400 hover:bg-white/5 h-8"><X className="w-4 h-4"/></Button>
                      <Button size="sm" onClick={()=>saveEdit(l.id)} data-testid={`loan-save-${l.id}`} className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 h-8 ml-1"><Save className="w-4 h-4"/></Button>
                    </>
                  ) : (
                    <>
                      <Button variant="ghost" size="sm" onClick={()=>startEdit(l)} data-testid={`loan-edit-${l.id}`} className="text-blue-400 hover:bg-blue-500/10 h-8"><Pencil className="w-4 h-4"/></Button>
                      <Button variant="ghost" size="sm" onClick={()=>del(l.id)} data-testid={`loan-del-${l.id}`} className="text-red-400 hover:bg-red-500/10 h-8 ml-1"><Trash2 className="w-4 h-4"/></Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
          {loaded && loans.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">No loans yet</TableCell></TableRow>}
          {!loaded && loading && <TableRow><TableCell colSpan={7} className="py-2"><LoadingSkeleton rows={3} label="Loading loans…" testid="loans-loading"/></TableCell></TableRow>}
        </TableBody>
      </Table>
      <div className="text-[11px] text-slate-500 mt-3 flex items-center gap-1">
        <GripVertical className="w-3 h-3"/> Same order shown on Landing page + Universal Add + Refer modals — drag any row to change it.
      </div>
    </div>
  );
}

function LoanIconPicker({ value, onChange, compact = false, testIdPrefix = "loan" }) {
  const entries = Object.entries(LOAN_ICON_MAP);
  return (
    <div className={`grid gap-2 ${compact ? "grid-cols-7" : "grid-cols-7"} mt-2`} data-testid={`${testIdPrefix}-icon-picker`}>
      {entries.map(([key, { Icon, label }]) => {
        const active = value === key;
        return (
          <button key={key} type="button" title={label} onClick={() => onChange(key)}
            data-testid={`${testIdPrefix}-icon-${key}`}
            className={`aspect-square rounded-md border flex items-center justify-center transition-colors ${active ? "bg-amber-500 border-amber-400 text-slate-900" : "bg-slate-950 border-slate-700 text-slate-400 hover:border-amber-500/50 hover:text-amber-400"}`}>
            <Icon className={compact ? "w-3.5 h-3.5" : "w-4 h-4"}/>
          </button>
        );
      })}
    </div>
  );
}

function guessIconKey(name = "") {
  const n = (name || "").toUpperCase();
  if (n.includes("HOME")) return "home";
  if (n.includes("LAP") || n.includes("PROP")) return "landmark";
  if (n.includes("GOLD")) return "gem";
  if (n.includes("TAKE OVER") || n === "TO") return "repeat";
  if (n.includes("BUSINESS")) return "briefcase";
  if (n.includes("VEHICLE") || n.includes("CAR")) return "car";
  if (n.includes("PERSONAL")) return "wallet";
  if (n.includes("AGRI") || n.includes("FARM")) return "tractor";
  if (n.includes("EDU")) return "graduation";
  if (n.includes("MED") || n.includes("HEALTH")) return "heart";
  return "coins";
}

// -- Banks -----------------------------------------------
function StatCard({ label, primary, secondary, accent = "amber", testid, onClick, active }) {
  const tones = {
    amber: "from-amber-500/15 to-slate-950 border-amber-500/30 text-amber-300",
    emerald: "from-emerald-500/15 to-slate-950 border-emerald-500/30 text-emerald-300",
    sky: "from-sky-500/15 to-slate-950 border-sky-500/30 text-sky-300",
    orange: "from-orange-500/15 to-slate-950 border-orange-500/40 text-orange-300",
  };
  const clickable = typeof onClick === "function";
  const activeCls = active ? " ring-2 ring-orange-400/70 shadow-lg shadow-orange-500/20" : "";
  return (
    <button
      type={clickable ? "button" : undefined}
      onClick={onClick}
      disabled={!clickable}
      data-testid={testid}
      className={`text-left rounded-xl bg-gradient-to-br ${tones[accent]} p-4 flex-1 min-w-[220px] border transition-all ${clickable ? "hover:brightness-110 cursor-pointer" : "cursor-default"}${activeCls}`}>
      <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">{label}</div>
      <div className="text-white font-heading text-2xl mt-1 leading-tight">{primary}</div>
      {secondary && <div className="text-[11px] text-slate-400 mt-1 leading-snug">{secondary}</div>}
      {clickable && <div className="text-[10px] font-bold mt-2 tracking-widest">{active ? "✓ Filter on — click to clear" : "Tap to filter →"}</div>}
    </button>
  );
}

function AssignStaffDrawer({ open, onClose, bank, district, onSaved }) {
  const [f, setF] = useState({ name: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ name: "", phone: "", password: "" }); }, [open, bank?.id, district]);
  if (!bank) return null;
  const save = async () => {
    if (!f.name.trim()) return toast.error("Name required");
    if (!/^\d{10}$/.test(f.phone || "")) return toast.error("Phone must be 10 digits");
    if (!f.password.trim()) return toast.error("Password required");
    setBusy(true);
    try {
      await api.post("/admin/bank-staff", {
        name: f.name.trim(), phone: f.phone.trim(), password: f.password.trim(),
        bank_id: bank.id, district: district || "", bank_commissions: {}, taluks: [],
      });
      toast.success(`${f.name} added · Status: NEW · ${bank.name} · ${district || "no district"}`);
      onSaved && onSaved();
      onClose();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-orange-500/40 text-white max-w-md" data-testid="assign-staff-drawer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-orange-400"/>
            Assign Staff · <span className="text-amber-400">{bank.name}</span>
            {district && <> · <span className="text-orange-300">{district}</span></>}
          </DialogTitle>
        </DialogHeader>
        <div className="rounded-lg bg-orange-500/10 border border-orange-500/30 p-3 text-xs text-orange-300 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5"/>
          <div>
            {district
              ? <>This bank has <b>0 staff in {district}</b>. New leads for this bank in {district} won't be picked up until you assign someone.</>
              : <>This bank has <b>0 staff overall</b>. Onboard the first staff so the bank can start receiving cases.</>}
          </div>
        </div>
        <div className="space-y-2 mt-2">
          <Label className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Create New Bank Staff</Label>
          <Input data-testid="assign-name" value={f.name} onChange={e=>setF({...f, name: e.target.value})} placeholder="Full name" className="bg-slate-950 border-slate-700 h-11"/>
          <Input data-testid="assign-phone" value={f.phone} onChange={e=>setF({...f, phone: e.target.value.replace(/\D/g,"").slice(0,10)})} placeholder="10-digit phone (login id)" className="bg-slate-950 border-slate-700 h-11 font-mono-alt"/>
          <Input data-testid="assign-password" value={f.password} onChange={e=>setF({...f, password: e.target.value})} placeholder="Initial password" className="bg-slate-950 border-slate-700 h-11" type="text"/>
          <div className="text-[10px] text-slate-500">
            Bank <b className="text-amber-300">{bank.name}</b> and district <b className="text-orange-300">{district || "—"}</b> are locked. Staff will finish photo / ID / branch / commission via their own Edit Profile.
          </div>
          <Button data-testid="assign-save" onClick={save} disabled={busy} className="w-full h-11 bg-orange-500 hover:bg-orange-400 text-slate-900 font-bold rounded-full mt-2">
            {busy ? "Saving…" : "Save · Create as NEW"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BankStaffDrilldown({ open, onClose, bank, districtCell }) {
  if (!bank || !districtCell) return null;
  const staff = districtCell.staff || [];
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg" data-testid="drill-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-amber-400"/>
            {bank.name} <span className="text-amber-400">·</span> {districtCell.district}
            <span className="ml-2 text-xs text-slate-500 font-normal">{staff.length} staff</span>
          </DialogTitle>
        </DialogHeader>
        {staff.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center">No active staff for this bank in this district yet.</div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {staff.map(s => (
              <div key={s.id} data-testid={`drill-staff-${s.id}`} className="flex items-center gap-3 rounded-lg bg-slate-950 border border-slate-800 p-3">
                <div className="w-9 h-9 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-300 flex items-center justify-center text-xs font-bold shrink-0">
                  {(s.name || "?").split(" ").map(x => x[0]).slice(0, 2).join("").toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-white font-semibold truncate">{s.name}</div>
                  <div className="text-[11px] text-slate-500 font-mono-alt">{s.phone || "—"}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[9px] uppercase text-slate-500 tracking-widest">Moving</div>
                  <div className="text-emerald-400 font-bold font-mono-alt">{s.moving_files}</div>
                </div>
                <Badge className={`ml-2 text-[10px] ${s.status === "active" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : s.status === "approval_waiting" ? "bg-orange-500/15 text-orange-300 border-orange-500/30" : "bg-yellow-500/15 text-yellow-300 border-yellow-500/30"}`}>
                  {(s.status || "new").replace("_", " ").toUpperCase()}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function fmtShortINR(amount) {
  const n = Number(amount || 0);
  if (n >= 1_00_00_000) return `₹${(n / 1_00_00_000).toFixed(1)}Cr`;
  if (n >= 1_00_000)   return `₹${(n / 1_00_000).toFixed(1)}L`;
  if (n >= 1_000)      return `₹${(n / 1_000).toFixed(1)}k`;
  return `₹${n.toLocaleString("en-IN")}`;
}

function BanksPage() {
  const [data, setData] = useState({ banks: [], summary: null, districts: [] });
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [f, setF] = useState({ name: "", loan_types: [] });
  const [open, setOpen] = useState(false);
  const [districtFilter, setDistrictFilter] = useState("all");
  const [zeroStaffOnly, setZeroStaffOnly] = useState(false);
  const [drill, setDrill] = useState(null); // { bank, districtCell }
  const [assign, setAssign] = useState(null); // { bank, district }
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (districtFilter && districtFilter !== "all") params.set("district", districtFilter);
      if (zeroStaffOnly) params.set("only_no_staff", "1");
      const qs = params.toString();
      const [ov, ln] = await Promise.all([
        api.get(`/admin/banks/overview${qs ? "?" + qs : ""}`),
        api.get("/loans"),
      ]);
      setData(ov.data);
      setLoans(ln.data);
      setLoaded(true);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [districtFilter, zeroStaffOnly]);
  useRefocus(load);
  const toggle = (arr, v) => arr.includes(v) ? arr.filter(x=>x!==v) : [...arr, v];
  const create = async () => {
    try { await api.post("/admin/banks", f); toast.success("Bank added"); setOpen(false); setF({name:"",loan_types:[]}); load(); } catch(e){toast.error(errorMessage(e));}
  };
  const del = async (id) => { if (!window.confirm("Delete this bank?")) return; await api.delete(`/admin/banks/${id}`); load(); };

  const summary = data.summary || { total_banks: 0, total_staff: 0, staff_by_district: {}, moving_amount: 0, moving_files: 0, moving_by_district: {}, banks_zero_staff: 0 };
  const topDistricts = (d) => Object.entries(d || {}).sort((a,b) => b[1]-a[1]).slice(0, 4);

  return (
    <div className="space-y-5">
      {/* Overall summary cards */}
      <div className="flex flex-wrap gap-3" data-testid="banks-summary">
        <StatCard
          testid="stat-total-banks"
          label="Total Banks / Finance"
          primary={summary.total_banks}
          secondary={summary.banks_zero_staff > 0 ? `${summary.banks_zero_staff} without any staff` : "All banks have staff"}
          accent="amber"
        />
        <StatCard
          testid="stat-total-staff"
          label="Total Staff (Active + In-flight)"
          primary={summary.total_staff}
          secondary={topDistricts(summary.staff_by_district).map(([d, n]) => `${d}: ${n}`).join(" · ") || "—"}
          accent="emerald"
        />
        <StatCard
          testid="stat-moving"
          label="Overall Moving Amount"
          primary={`${fmtShortINR(summary.moving_amount)} (${summary.moving_files} files)`}
          secondary={topDistricts(summary.moving_by_district).map(([d, v]) => `${d}: ${fmtShortINR(v)}`).join(" · ") || "No moving files"}
          accent="sky"
        />
        <StatCard
          testid="stat-zero-staff"
          label="Banks with 0 Staff (Attention)"
          primary={summary.banks_zero_staff}
          secondary={summary.banks_zero_staff > 0 ? "Assign bank staff to unlock leads" : "You're all set"}
          accent={summary.banks_zero_staff > 0 ? "orange" : "emerald"}
          onClick={summary.banks_zero_staff > 0 ? () => setZeroStaffOnly(v => !v) : undefined}
          active={zeroStaffOnly}
        />
      </div>

      <div className="card-surface p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="font-heading text-xl text-white">Bank Partners</div>
            <div className="text-sm text-slate-500">Districts, staff coverage & live moving amount.</div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={load}
              data-testid="banks-refresh"
              title="Refresh"
              className="w-9 h-9 rounded-md bg-slate-800 hover:bg-slate-700 border border-slate-700 flex items-center justify-center text-slate-400"
            ><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}/></button>
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-bank-btn" className="bg-amber-500 hover:bg-amber-400 text-slate-900 rounded-full"><Plus className="w-4 h-4 mr-2"/>Add Bank</Button></DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg">
              <DialogHeader><DialogTitle>New Bank</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Input data-testid="bank-name" placeholder="Bank name" value={f.name} onChange={e=>setF({...f,name:e.target.value})} className="bg-slate-950 border-slate-700"/>
                <div>
                  <Label className="text-slate-400 text-xs uppercase tracking-widest">Loan Types</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {loans.map(l => (
                      <button key={l.id} type="button" onClick={()=>setF({...f, loan_types: toggle(f.loan_types, l.name)})} className={`px-3 py-1 text-xs rounded-full border ${f.loan_types.includes(l.name)?"bg-amber-500 border-amber-500 text-slate-900":"border-slate-700 text-slate-400"}`}>{l.name}</button>
                    ))}
                  </div>
                </div>
                <Button data-testid="bank-save" onClick={create} className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full">Save Bank</Button>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* District Filter */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Filter by District:</Label>
          <Select value={districtFilter} onValueChange={setDistrictFilter}>
            <SelectTrigger data-testid="district-filter" className="w-56 bg-slate-950 border-slate-700 h-9 text-sm">
              <SelectValue/>
            </SelectTrigger>
            <SelectContent className="bg-slate-900 text-white border-slate-700 max-h-72">
              <SelectItem value="all" data-testid="district-opt-all">All Districts</SelectItem>
              {(data.districts || []).map(d => (
                <SelectItem key={d} value={d} data-testid={`district-opt-${d}`}>{d}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {districtFilter !== "all" && (
            <button
              type="button"
              onClick={() => setDistrictFilter("all")}
              data-testid="district-clear"
              className="text-[10px] text-slate-400 hover:text-amber-400 uppercase tracking-widest">
              Clear
            </button>
          )}
          {zeroStaffOnly && (
            <div className="ml-auto inline-flex items-center gap-2 rounded-full bg-orange-500/15 border border-orange-500/40 text-orange-300 px-3 py-1 text-[11px] font-bold uppercase tracking-widest" data-testid="zero-staff-chip">
              <AlertTriangle className="w-3 h-3"/> Fill Pending View
              <button type="button" onClick={() => setZeroStaffOnly(false)} data-testid="zero-staff-clear" className="ml-1 text-orange-200 hover:text-white">×</button>
            </div>
          )}
        </div>

        <Table>
          <TableHeader>
            <TableRow className="border-white/5">
              <TableHead>Name</TableHead>
              <TableHead>Districts (staff)</TableHead>
              <TableHead>Total Staff</TableHead>
              <TableHead>Moving Files Amount</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data.banks || []).map(b => (
              <TableRow key={b.id} className="border-white/5" data-testid={`bank-row-${b.id}`}>
                <TableCell className="text-white font-semibold">{b.name}</TableCell>
                <TableCell>
                  {b.districts.length === 0 ? (
                    <button
                      type="button"
                      onClick={() => setAssign({ bank: b, district: "" })}
                      data-testid={`bank-no-districts-${b.id}`}
                      className="inline-flex items-center gap-1 text-orange-300 text-xs hover:text-orange-200 underline-offset-2 hover:underline">
                      <AlertTriangle className="w-3.5 h-3.5"/> No districts yet · Add staff
                    </button>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {b.districts.map(d => {
                        const empty = d.staff_count === 0;
                        return (
                          <button
                            key={d.district}
                            type="button"
                            onClick={() => empty ? setAssign({ bank: b, district: d.district }) : setDrill({ bank: b, districtCell: d })}
                            data-testid={`bank-district-${b.id}-${d.district}`}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] transition-colors ${
                              empty
                                ? "bg-red-500/15 border-red-500/50 text-red-300 hover:bg-red-500/25 animate-pulse"
                                : "bg-emerald-500/10 border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20"
                            }`}>
                            {empty && <AlertTriangle className="w-3 h-3 text-red-400"/>}
                            <span className="font-bold">{d.district}</span> <span className="opacity-70">({d.staff_count} staff)</span>
                            {empty && <span className="ml-1 uppercase text-[9px] tracking-widest font-black">No staff · Click to Assign</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <button
                    type="button"
                    onClick={() => setAssign({ bank: b, district: districtFilter !== "all" ? districtFilter : "" })}
                    disabled={b.total_staff > 0 && !(districtFilter !== "all" && b.filter_staff_count === 0)}
                    data-testid={`bank-total-staff-${b.id}`}
                    className={`inline-flex items-center gap-1 font-mono-alt font-bold rounded-md px-2 py-0.5 ${
                      b.total_staff === 0
                        ? "text-red-300 bg-red-500/10 border border-red-500/40 hover:bg-red-500/20 animate-pulse cursor-pointer"
                        : "text-white cursor-default"
                    }`}>
                    <Users2 className="w-3.5 h-3.5"/> {b.total_staff}
                    {b.total_staff === 0 && <span className="ml-1 uppercase text-[9px] tracking-widest">Fill →</span>}
                  </button>
                </TableCell>
                <TableCell>
                  {b.moving_files === 0 ? (
                    <span className="text-slate-600 text-xs">—</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => nav(`/admin?tab=moving_files&bank=${encodeURIComponent(b.name)}`)}
                      data-testid={`bank-moving-${b.id}`}
                      className="text-emerald-400 font-mono-alt font-bold hover:underline">
                      {fmtShortINR(b.moving_amount)} <span className="text-slate-500 font-normal">({b.moving_files} files)</span>
                    </button>
                  )}
                </TableCell>
                <TableCell className="text-slate-400">{b.contact || "—"}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {(b.total_staff === 0 || (districtFilter !== "all" && b.filter_staff_count === 0)) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setAssign({ bank: b, district: districtFilter !== "all" ? districtFilter : "" })}
                        data-testid={`bank-quick-add-${b.id}`}
                        className="text-orange-300 hover:bg-orange-500/10 h-8 px-2 border border-orange-500/40 rounded-full text-[11px] font-bold">
                        <UserPlus className="w-3.5 h-3.5 mr-1"/> Add Staff
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={()=>del(b.id)} data-testid={`bank-del-${b.id}`} className="text-red-400 hover:bg-red-500/10">
                      <Trash2 className="w-4 h-4"/>
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {loaded && (data.banks || []).length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                  {zeroStaffOnly ? "All banks have staff — nothing to fill" : (districtFilter === "all" ? "No banks yet" : `No banks operate in ${districtFilter}`)}
                </TableCell>
              </TableRow>
            )}
            {!loaded && loading && (
              <TableRow>
                <TableCell colSpan={6} className="py-2"><LoadingSkeleton rows={4} label="Loading banks…" testid="banks-loading"/></TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <BankStaffDrilldown open={!!drill} onClose={() => setDrill(null)} bank={drill?.bank} districtCell={drill?.districtCell}/>
      <AssignStaffDrawer open={!!assign} onClose={() => setAssign(null)} bank={assign?.bank} district={assign?.district} onSaved={load}/>
    </div>
  );
}

// -- Bank Staff -----------------------------------------------
function StatusBadge({ staff, onClick }) {
  const now = Date.now();
  let mode = staff.status || (staff.banned_until ? "suspended" : "active");
  let daysLeft = 0;
  if (mode === "suspended" && staff.suspended_until) {
    const until = new Date(staff.suspended_until).getTime();
    if (until > now) daysLeft = Math.max(1, Math.ceil((until - now) / 86400000));
    else mode = "active";
  }
  const style =
    mode === "terminated" ? "bg-red-500/20 text-red-400 border-red-500/40 hover:bg-red-500/30"
    : mode === "suspended" ? "bg-amber-500/20 text-amber-400 border-amber-500/40 hover:bg-amber-500/30"
    : mode === "new" ? "bg-yellow-500/20 text-yellow-300 border-yellow-500/40 hover:bg-yellow-500/30"
    : mode === "approval_waiting" ? "bg-orange-500/25 text-orange-300 border-orange-500/50 hover:bg-orange-500/35"
    : "bg-emerald-500/20 text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/30";
  const label =
    mode === "terminated" ? "Terminated"
    : mode === "suspended" ? `Suspended · ${daysLeft}d left`
    : mode === "new" ? "NEW"
    : mode === "approval_waiting" ? "Approval Waiting"
    : "Active";
  return (
    <button onClick={onClick} data-testid={`status-btn-${staff.id}`} className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors ${style}`}>
      {mode === "approval_waiting" && (
        <span className="relative flex h-2 w-2" data-testid={`blink-${staff.id}`}>
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-400"></span>
        </span>
      )}
      {label}
    </button>
  );
}

// -- Bank Staff Profile Review Dialog (Side-by-Side) --------------------
function ReviewProfileDialog({ staff, open, onClose, onDone, branchName }) {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const req = staff?.pending_profile;
  useEffect(() => { if (open) { setRejecting(false); setReason(""); } }, [open]);
  if (!staff || !req) return null;

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/bank-staff/profile-requests/${req.id}/approve`);
      toast.success(`${staff.name} approved · Now ACTIVE`);
      onDone();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setBusy(false); }
  };
  const reject = async () => {
    if (!reason.trim()) return toast.error("Please provide a rejection reason");
    setBusy(true);
    try {
      await api.post(`/admin/bank-staff/profile-requests/${req.id}/reject`, { reason: reason.trim() });
      toast.success("Rejected — reason sent to staff");
      onDone();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setBusy(false); }
  };

  const oldCol = {
    photo: staff.photo_b64 || "",
    id_card: staff.id_card_b64 || "",
    branch: staff.branch_name || "—",
    loan_types: staff.loan_types || [],
    min_amount: staff.min_amount || 0,
    max_amount: staff.max_amount || 0,
    connector_commissions: staff.connector_commissions || {},
    lat: staff.branch_lat,
    lng: staff.branch_lng,
  };
  const newCol = {
    photo: req.photo_b64 || "",
    id_card: req.id_card_b64 || "",
    branch: branchName || req.branch_id || "—",
    loan_types: req.loan_types || [],
    min_amount: req.min_amount || 0,
    max_amount: req.max_amount || 0,
    connector_commissions: req.connector_commissions || {},
    lat: req.branch_lat,
    lng: req.branch_lng,
  };
  const fmtAmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

  const Col = ({ title, data, tone, testid }) => (
    <div className={`flex-1 rounded-xl border ${tone} p-3 space-y-3`} data-testid={testid}>
      <div className={`text-[10px] font-black uppercase tracking-widest ${tone.includes("emerald") ? "text-emerald-400" : "text-slate-400"}`}>{title}</div>
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-16 h-16 rounded-full bg-slate-950 border border-slate-700 overflow-hidden flex items-center justify-center">
          {data.photo ? <img src={data.photo} alt="photo" className="w-full h-full object-cover"/> : <div className="text-[10px] text-slate-600">No Photo</div>}
        </div>
        <div className="text-xs text-slate-300 flex-1">
          <div className="text-slate-500 text-[10px] uppercase">Branch</div>
          <div className="font-semibold text-white">{data.branch}</div>
        </div>
      </div>
      <div>
        <div className="text-slate-500 text-[10px] uppercase mb-1">ID Card</div>
        {data.id_card ? (
          data.id_card.startsWith("data:application/pdf")
            ? <a href={data.id_card} target="_blank" rel="noreferrer" className="text-blue-400 underline text-xs">Open PDF</a>
            : <img src={data.id_card} alt="id" className="w-full h-24 object-cover rounded-md border border-slate-700"/>
        ) : <div className="text-[11px] text-slate-600">—</div>}
      </div>
      <div>
        <div className="text-slate-500 text-[10px] uppercase mb-1">Loan Types</div>
        <div className="flex flex-wrap gap-1">
          {data.loan_types.length ? data.loan_types.map(lt => <Badge key={lt} className="bg-slate-800 text-slate-300 border-slate-700 text-[10px]">{lt}</Badge>) : <span className="text-[11px] text-slate-600">—</span>}
        </div>
      </div>
      <div>
        <div className="text-slate-500 text-[10px] uppercase mb-1">Amount Range (min–max)</div>
        <div className="text-white font-mono-alt">{fmtAmt(data.min_amount)} → {fmtAmt(data.max_amount)}</div>
      </div>
      <div>
        <div className="text-slate-500 text-[10px] uppercase mb-1">Staff Commission to Sakthi</div>
        {Object.keys(data.connector_commissions).length ? (
          <div className="space-y-1 text-[11px]">
            {Object.entries(data.connector_commissions).map(([k,v]) => {
              const rich = typeof v === "object" && v !== null;
              const pct = rich ? v.pct : v;
              const mn = rich ? v.min : null;
              const mx = rich ? v.max : null;
              return (
                <div key={k} className="flex items-center justify-between bg-slate-950 px-2 py-1.5 rounded border border-slate-800">
                  <span className="text-slate-300 font-semibold">{k}</span>
                  <span className="text-amber-400 font-bold font-mono-alt">{pct || 0}%</span>
                  <span className="text-slate-400 text-[10px]">{mn != null || mx != null ? `${fmtAmt(mn || 0)}–${fmtAmt(mx || 0)}` : "any"}</span>
                </div>
              );
            })}
          </div>
        ) : <div className="text-[11px] text-slate-600">—</div>}
      </div>
      <div>
        <div className="text-slate-500 text-[10px] uppercase mb-1">GPS Location</div>
        {data.lat != null && data.lng != null ? (
          <>
            <div className="text-[10px] text-slate-400 font-mono-alt mb-1">{data.lat}, {data.lng}</div>
            <iframe title={`map-${title}`} className="w-full h-28 rounded-md border border-slate-700" src={`https://maps.google.com/maps?q=${data.lat},${data.lng}&z=15&output=embed`} loading="lazy"/>
          </>
        ) : <div className="text-[11px] text-slate-600">Not pinned</div>}
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-orange-500/40 text-white max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="review-profile-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="animate-pulse w-2 h-2 rounded-full bg-orange-400"></span>
            Review Profile Update — <span className="text-orange-300">{staff.name}</span>
            <span className="text-xs text-slate-500 font-normal ml-2">{staff.phone} · {staff.district}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col md:flex-row gap-3 mt-2">
          <Col title="OLD DATA" data={oldCol} tone="border-slate-800 bg-slate-950/60" testid="review-old"/>
          <Col title="NEW (PENDING)" data={newCol} tone="border-emerald-500/40 bg-emerald-500/5" testid="review-new"/>
        </div>
        {!rejecting ? (
          <div className="grid grid-cols-2 gap-2 mt-4">
            <Button data-testid="approve-profile-btn" onClick={approve} disabled={busy}
              className="h-11 rounded-full font-bold bg-emerald-500 hover:bg-emerald-400 text-slate-900">
              <CheckCircle2 className="w-4 h-4 mr-2"/> Approve & Make Active
            </Button>
            <Button data-testid="show-reject-btn" onClick={() => setRejecting(true)} disabled={busy}
              variant="outline" className="h-11 rounded-full font-bold border-red-500/50 text-red-400 hover:bg-red-500/10">
              <XCircle className="w-4 h-4 mr-2"/> Reject with Reason
            </Button>
          </div>
        ) : (
          <div className="mt-4 space-y-2" data-testid="reject-panel">
            <Label className="text-xs uppercase tracking-widest text-slate-400">Reason <span className="text-red-400">*</span></Label>
            <Textarea data-testid="reject-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this profile update being rejected?" className="bg-slate-950 border-slate-700 min-h-[80px]"/>
            <div className="grid grid-cols-2 gap-2">
              <Button data-testid="cancel-reject-btn" variant="outline" onClick={() => setRejecting(false)} className="h-10 rounded-full border-slate-700 text-slate-300">Cancel</Button>
              <Button data-testid="confirm-reject-btn" onClick={reject} disabled={busy} className="h-10 rounded-full bg-red-500 hover:bg-red-400 text-white font-bold">Confirm Reject</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditStaffDialog({ staff, banks = [], districts = [], open, onClose, onSaved }) {
  const isBank = staff?.role === "bank_staff";
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open || !staff) return;
    setF({
      name: staff.name || "",
      phone: staff.phone || "",
      email: staff.email || "",
      district: staff.district || staff.managed_district || "",
      bank_id: staff.bank_id || "",
      sub_role: staff.sub_role || "",
      monthly_salary: staff.monthly_salary || "",
    });
  }, [open, staff]);
  const save = async () => {
    if (!f.name?.trim()) return toast.error("Name is required");
    if (!/^\d{10}$/.test(f.phone || "")) return toast.error("Phone must be exactly 10 digits");
    setSaving(true);
    try {
      const payload = {
        name: f.name.trim(),
        phone: f.phone,
        email: (f.email || "").trim(),
        district: f.district || undefined,
      };
      if (isBank) {
        payload.bank_id = f.bank_id || undefined;
      } else {
        payload.sub_role = f.sub_role || undefined;
        if (f.monthly_salary !== "" && f.monthly_salary !== null) {
          payload.monthly_salary = Number(f.monthly_salary);
        }
      }
      await api.put(`/admin/users/${staff.id}`, payload);
      toast.success("Staff details updated successfully");
      onSaved && onSaved();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setSaving(false); }
  };
  if (!staff) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md max-h-[90vh] overflow-y-auto" data-testid="edit-staff-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-400">
            <Pencil className="w-4 h-4"/> Edit {isBank ? "Bank Staff" : "Office Staff"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-slate-400 text-xs uppercase tracking-widest">Name</Label>
            <Input data-testid="edit-name" value={f.name || ""} onChange={e => setF({...f, name: e.target.value})} className="bg-slate-950 border-slate-700 mt-2"/>
          </div>
          <div>
            <Label className="text-slate-400 text-xs uppercase tracking-widest">Phone (Login ID)</Label>
            <Input data-testid="edit-phone" maxLength={10} value={f.phone || ""}
              onChange={e => setF({...f, phone: e.target.value.replace(/\D/g,"")})}
              className="bg-slate-950 border-slate-700 mt-2 font-mono-alt"/>
            <div className="text-[10px] text-slate-500 mt-1">10-digit mobile · used for login</div>
          </div>
          <div>
            <Label className="text-slate-400 text-xs uppercase tracking-widest">Email</Label>
            <Input data-testid="edit-email" type="email" value={f.email || ""} onChange={e => setF({...f, email: e.target.value})} className="bg-slate-950 border-slate-700 mt-2"/>
          </div>
          <div>
            <Label className="text-slate-400 text-xs uppercase tracking-widest">District</Label>
            <Select value={f.district || ""} onValueChange={v => setF({...f, district: v})}>
              <SelectTrigger data-testid="edit-district" className="bg-slate-950 border-slate-700 mt-2 w-full"><SelectValue placeholder="Select district"/></SelectTrigger>
              <SelectContent className="bg-slate-900 text-white border-slate-700 max-h-64 min-w-[--radix-select-trigger-width]">
                {districts.map(d => <SelectItem key={d.id || d.name} value={d.name}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {isBank ? (
            <div>
              <Label className="text-slate-400 text-xs uppercase tracking-widest">Bank / Branch</Label>
              <Select value={f.bank_id || ""} onValueChange={v => setF({...f, bank_id: v})}>
                <SelectTrigger data-testid="edit-bank" className="bg-slate-950 border-slate-700 mt-2 w-full"><SelectValue placeholder="Select bank"/></SelectTrigger>
                <SelectContent className="bg-slate-900 text-white border-slate-700 max-h-64 min-w-[--radix-select-trigger-width]">
                  {banks.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <>
              <div>
                <Label className="text-slate-400 text-xs uppercase tracking-widest">Role</Label>
                <Select value={f.sub_role || ""} onValueChange={v => setF({...f, sub_role: v})}>
                  <SelectTrigger data-testid="edit-sub-role" className="bg-slate-950 border-slate-700 mt-2 w-full"><SelectValue placeholder="Select role"/></SelectTrigger>
                  <SelectContent className="bg-slate-900 text-white border-slate-700">
                    <SelectItem value="telecaller">Telecaller</SelectItem>
                    <SelectItem value="field">Field</SelectItem>
                    <SelectItem value="district_manager">District Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-400 text-xs uppercase tracking-widest">Monthly Salary (₹)</Label>
                <div className="relative mt-2">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">₹</span>
                  <Input data-testid="edit-salary" type="number" value={f.monthly_salary || ""}
                    onChange={e => setF({...f, monthly_salary: e.target.value.replace(/\D/g,"")})}
                    className="bg-slate-950 border-slate-700 pl-8"/>
                </div>
              </div>
            </>
          )}
          <Button data-testid="edit-save-btn" onClick={save} disabled={saving}
            className="w-full mt-2 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full">
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ staff, open, onClose, onSaved }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setPw(""); setPw2(""); } }, [open]);
  const save = async () => {
    if (pw.length < 4) return toast.error("Password must be at least 4 characters");
    if (pw !== pw2) return toast.error("Passwords do not match");
    setSaving(true);
    try {
      await api.post(`/admin/users/${staff.id}/reset-password`, { new_password: pw });
      toast.success(`Password reset successful for ${staff.name}`);
      onSaved && onSaved();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setSaving(false); }
  };
  if (!staff) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md" data-testid="reset-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-400">
            <KeyRound className="w-4 h-4"/> Reset Password — {staff.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-100">
            Type a new password. Staff can login with this immediately. Ask them to change it after login.
          </div>
          <div>
            <Label className="text-slate-400 text-xs uppercase tracking-widest">New Password</Label>
            <Input data-testid="new-pw" type="password" autoFocus value={pw} onChange={e => setPw(e.target.value)}
              className="bg-slate-950 border-slate-700 mt-2"/>
          </div>
          <div>
            <Label className="text-slate-400 text-xs uppercase tracking-widest">Confirm Password</Label>
            <Input data-testid="confirm-pw" type="password" value={pw2} onChange={e => setPw2(e.target.value)}
              className="bg-slate-950 border-slate-700 mt-2"/>
          </div>
          <Button data-testid="reset-pw-save" onClick={save} disabled={saving || !pw || pw !== pw2}
            className="w-full mt-2 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full disabled:opacity-40">
            {saving ? "Resetting…" : "Reset Password"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StatusDialog({ staff, endpoint = "/admin/bank-staff", open, onClose, onSaved }) {
  const [mode, setMode] = useState(staff?.status || "active");
  const [preset, setPreset] = useState("7");
  const [custom, setCustom] = useState("");
  const [reason, setReason] = useState("");
  useEffect(() => { if (open) { setMode(staff?.status || "active"); setPreset("7"); setCustom(""); setReason(""); } }, [open, staff]);
  const save = async () => {
    if (mode !== "active" && !reason.trim()) return toast.error("Please provide a reason");
    try {
      const body = { status: mode, reason: reason.trim() };
      if (mode === "suspended") body.days = preset === "custom" ? parseInt(custom || 0, 10) : parseInt(preset, 10);
      await api.post(`${endpoint}/${staff.id}/status`, body);
      toast.success("Status updated");
      onSaved();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  if (!staff) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md max-h-[90vh] overflow-y-auto" data-testid="status-dialog">
        <DialogHeader><DialogTitle>{staff.name} · Status</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {[
            { id: "active", Icon: ShieldCheck, title: "Active", desc: "Staff can login normally", sel: "bg-emerald-500/10 border-emerald-500/40", icon: "text-emerald-400" },
            { id: "suspended", Icon: Pause, title: "Suspend", desc: "Temporarily block login for a duration", sel: "bg-amber-500/10 border-amber-500/40", icon: "text-amber-400" },
            { id: "terminated", Icon: XOctagon, title: "Terminate / Permanently Block", desc: "Staff can never login again", sel: "bg-red-500/10 border-red-500/40", icon: "text-red-400" },
          ].map((o) => (
            <button key={o.id} data-testid={`status-option-${o.id}`} onClick={() => setMode(o.id)}
              className={`w-full text-left p-4 rounded-xl border flex gap-4 items-start transition-colors ${mode === o.id ? o.sel : "border-slate-800 hover:border-slate-700"}`}>
              <o.Icon className={`w-5 h-5 mt-0.5 ${o.icon}`} />
              <div><div className="font-heading text-white">{o.title}</div><div className="text-xs text-slate-400 mt-0.5">{o.desc}</div></div>
            </button>
          ))}
          {mode === "suspended" && (
            <div className="pt-3 border-t border-slate-800" data-testid="suspend-duration">
              <Label className="text-slate-400 text-xs uppercase tracking-widest">Duration</Label>
              <div className="grid grid-cols-4 gap-2 mt-2">
                {["7", "15", "30", "60"].map((d) => (
                  <button key={d} onClick={() => setPreset(d)} data-testid={`suspend-${d}d`} className={`px-3 py-2 rounded-lg text-sm border ${preset === d ? "bg-amber-500 text-slate-900 border-amber-500" : "border-slate-700 text-slate-300 hover:border-slate-600"}`}>{d} days</button>
                ))}
              </div>
              <button onClick={() => setPreset("custom")} data-testid="suspend-custom-btn" className={`mt-2 w-full px-3 py-2 rounded-lg text-sm border ${preset === "custom" ? "bg-amber-500 text-slate-900 border-amber-500" : "border-slate-700 text-slate-300 hover:border-slate-600"}`}>Custom</button>
              {preset === "custom" && (
                <Input data-testid="suspend-custom-input" placeholder="Days" value={custom} onChange={(e) => setCustom(e.target.value.replace(/\D/g, ""))} className="mt-2 bg-slate-950 border-slate-700" />
              )}
            </div>
          )}
          {mode !== "active" && (
            <div className="pt-3 border-t border-slate-800">
              <Label className="text-slate-400 text-xs uppercase tracking-widest">Reason <span className="text-red-400">*</span></Label>
              <Textarea data-testid="status-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder={mode === "suspended" ? "Why is this staff being suspended?" : "Why is this staff being terminated?"} className="mt-2 bg-slate-950 border-slate-700 min-h-[80px]" />
            </div>
          )}
          <Button data-testid="status-save-btn" onClick={save} className="w-full mt-2 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full">Save Status</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BankStaffPage() {
  const [staff, setStaff] = useState([]);
  const [banks, setBanks] = useState([]);
  const [allBranches, setAllBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [statusStaff, setStatusStaff] = useState(null);
  const [reviewStaff, setReviewStaff] = useState(null);
  const [editStaff, setEditStaff] = useState(null);
  const [resetStaff, setResetStaff] = useState(null);
  const [tab, setTab] = useState("all"); // all | new | approval_waiting | active
  const impersonate = async (s) => {
    try {
      const { data } = await api.post(`/admin/impersonate/${s.id}`);
      beginImpersonation({ token: data.token, user: data.user });
      toast.success(`Viewing as ${s.name}`);
      const map = { bank_staff: "/bank", office_staff: "/staff", partner: "/partner" };
      window.location.href = map[s.role] || "/";
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const [f, setF] = useState({ name:"", phone:"", password:"", bank_id:"", district:"", bank_commissions: {}, branch_id:"" });
  const [open, setOpen] = useState(false);
  const [branches, setBranches] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const { districts: locDistricts } = useLocations();
  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("page_size", String(pageSize));
      if (debouncedSearch) params.set("q", debouncedSearch);
      if (tab !== "all") params.set("status", tab);
      const [s, b, br] = await Promise.all([
        api.get(`/admin/bank-staff?${params.toString()}`),
        api.get("/banks"),
        api.get("/branches").catch(() => ({ data: [] })),
      ]);
      // Backend returns paginated envelope { items, total, ... } when page_size>0
      const payload = s.data && typeof s.data === "object" && Array.isArray(s.data.items) ? s.data : { items: s.data || [], total: (s.data || []).length };
      setStaff(payload.items);
      setTotal(payload.total || 0);
      setBanks(b.data || []);
      setAllBranches(br.data || []);
      setLoaded(true);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, debouncedSearch, tab]);
  useRefocus(load);
  const branchNameById = (id) => (allBranches.find(b => b.id === id) || {}).branch_name || "";
  const effectiveStatus = (s) => {
    const m = s.status || (s.banned_until ? "suspended" : "active");
    return m;
  };
  const counts = staff.reduce((a, s) => {
    const m = effectiveStatus(s);
    a.all += 1;
    if (m === "new") a.new += 1;
    else if (m === "approval_waiting") a.approval_waiting += 1;
    else if (m === "active") a.active += 1;
    return a;
  }, { all: 0, new: 0, approval_waiting: 0, active: 0 });
  const [search, setSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => { setPage(1); }, [debouncedSearch, tab]);
  const q = search.trim().toLowerCase();
  const filtered = staff
    .filter(s => tab === "all" ? true : effectiveStatus(s) === tab)
    .filter(s => !q ||
      (s.name || "").toLowerCase().includes(q) ||
      (s.phone || "").toLowerCase().includes(q) ||
      (s.district || "").toLowerCase().includes(q) ||
      (s.bank_name || "").toLowerCase().includes(q) ||
      (s.branch_name || "").toLowerCase().includes(q)
    )
    .sort((a, b) => {
      const rank = (s) => effectiveStatus(s) === "approval_waiting" ? 0 : effectiveStatus(s) === "new" ? 1 : 2;
      return rank(a) - rank(b);
    });
  useEffect(() => {
    if (!f.district) { setBranches([]); return; }
    const d = locDistricts.find(x => x.name === f.district);
    if (!d) { setBranches([]); return; }
    api.get(`/districts/${d.id}/branches`).then(r => setBranches(r.data)).catch(() => setBranches([]));
  }, [f.district, locDistricts]);
  const create = async () => {
    if (!f.name?.trim()) return toast.error("Name required");
    if (!/^\d{10}$/.test(f.phone || "")) return toast.error("Phone must be 10 digits");
    if (!f.password?.trim()) return toast.error("Password required");
    if (!f.bank_id) return toast.error("Pick a Bank");
    if (!f.district) return toast.error("Pick a District");
    if (!f.branch_id) return toast.error("Pick a Branch");
    try{
      await api.post("/admin/bank-staff", { ...f, taluks: [], bank_commissions: {} });
      toast.success("Bank staff added · Status: NEW");
      setOpen(false);
      setF({name:"",phone:"",password:"",bank_id:"",district:"", bank_commissions:{}, branch_id:""});
      load();
    } catch(e){toast.error(errorMessage(e));}
  };
  return (
    <div className="card-surface p-6">
      <div className="flex items-center justify-between mb-6">
        <div><div className="font-heading text-xl text-white">Bank Staff</div><div className="text-sm text-slate-500">Login IDs are their phone numbers.</div></div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            data-testid="bstaff-refresh"
            title="Refresh"
            className="w-9 h-9 rounded-md bg-slate-800 hover:bg-slate-700 border border-slate-700 flex items-center justify-center text-slate-400"
          ><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}/></button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-bank-staff-btn" className="bg-amber-500 hover:bg-amber-400 text-slate-900 rounded-full"><Plus className="w-4 h-4 mr-2"/>Add Staff</Button></DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>New Bank Staff</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input data-testid="bstaff-name" placeholder="Name" value={f.name} onChange={e=>setF({...f,name:e.target.value})} className="bg-slate-950 border-slate-700"/>
              <Input data-testid="bstaff-phone" placeholder="Phone (Login ID)" value={f.phone} maxLength={10} onChange={e=>setF({...f,phone:e.target.value.replace(/\D/g,'')})} className="bg-slate-950 border-slate-700"/>
              <Input data-testid="bstaff-password" placeholder="Set password" value={f.password} onChange={e=>setF({...f,password:e.target.value})} className="bg-slate-950 border-slate-700"/>
              <Select value={f.bank_id} onValueChange={v=>setF({...f, bank_id:v, bank_commissions:{}})}>
                <SelectTrigger data-testid="bstaff-bank" className="bg-slate-950 border-slate-700 w-full"><SelectValue placeholder="Bank"/></SelectTrigger>
                <SelectContent className="bg-slate-900 text-white border-slate-700 w-full min-w-[--radix-select-trigger-width]">{banks.map(b=><SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={f.district} onValueChange={v=>setF({...f, district:v, branch_id:""})}>
                <SelectTrigger data-testid="bstaff-district" className="bg-slate-950 border-slate-700 w-full"><SelectValue placeholder="District"/></SelectTrigger>
                <SelectContent className="bg-slate-900 text-white border-slate-700 max-h-64 w-full min-w-[--radix-select-trigger-width]">{locDistricts.length === 0 ? <div className="p-3 text-sm text-slate-400">No districts configured. Go to Location Master.</div> : locDistricts.map(d=><SelectItem key={d.id} value={d.name} className="whitespace-nowrap">{d.name}</SelectItem>)}</SelectContent>
              </Select>

              <Select value={f.branch_id} onValueChange={v=>setF({...f, branch_id:v})} disabled={!f.district}>
                <SelectTrigger data-testid="bstaff-branch" className="bg-slate-950 border-slate-700 w-full disabled:opacity-50"><SelectValue placeholder={f.district ? "Branch (cascading)" : "Pick district first"}/></SelectTrigger>
                <SelectContent className="bg-slate-900 text-white border-slate-700 max-h-64 w-full min-w-[--radix-select-trigger-width]">
                  {branches.length === 0 ? <div className="p-3 text-sm text-slate-400">No branches. Ask admin.</div> : branches.map(b=><SelectItem key={b.id} value={b.id}>{b.branch_name}</SelectItem>)}
                </SelectContent>
              </Select>

              <div className="rounded-lg bg-slate-950/60 border border-slate-800 p-3 text-xs text-slate-400" data-testid="commission-hint">
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 mt-1.5 shrink-0"/>
                  <span>Photo, ID card, Loan Types, Commission % and GPS are set by the bank staff in their own <b className="text-yellow-300">Edit Profile</b> page (goes to Admin approval).</span>
                </div>
              </div>

              <Button data-testid="bstaff-save" onClick={create} className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full">Save · Create as NEW</Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>
      {/* Status filter tabs + search */}
      <div className="flex flex-wrap gap-2 mb-4 items-center" data-testid="bstaff-filter-tabs">
        {[
          { id: "all", label: "All", count: counts.all, cls: "bg-slate-800 text-slate-200 border-slate-700" },
          { id: "new", label: "NEW", count: counts.new, cls: "bg-yellow-500/15 text-yellow-300 border-yellow-500/40" },
          { id: "approval_waiting", label: "Approval Waiting", count: counts.approval_waiting, cls: "bg-orange-500/20 text-orange-300 border-orange-500/50" },
          { id: "active", label: "Active", count: counts.active, cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
            className={`px-3 py-1.5 rounded-full border text-xs font-bold uppercase tracking-widest transition-colors ${tab === t.id ? t.cls : "bg-slate-950 text-slate-500 border-slate-800 hover:border-slate-700"}`}>
            {t.label} · {t.count}
          </button>
        ))}
        <input
          data-testid="bstaff-search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search name / phone / bank / branch / district"
          className="ml-auto bg-slate-950 border border-slate-700 rounded-md px-3 h-9 text-sm text-white placeholder:text-slate-500 focus:border-amber-500 outline-none min-w-[280px]"
        />
      </div>
      <Table>
        <TableHeader><TableRow className="border-white/5">
          <TableHead>Name</TableHead>
          <TableHead>Phone</TableHead>
          <TableHead>District</TableHead>
          <TableHead>Bank / Finance &amp; Branch</TableHead>
          <TableHead>Referral</TableHead>
          <TableHead>OK Cases / Disbursed</TableHead>
          <TableHead>Score</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {filtered.map(s=>{
            const mode = effectiveStatus(s);
            const rowHl = mode === "approval_waiting" ? "bg-orange-500/5 hover:bg-orange-500/10" : "";
            return (
              <TableRow key={s.id} className={`border-white/5 ${rowHl}`} data-testid={`bstaff-row-${s.id}`}>
                <TableCell>
                  <button
                    type="button"
                    onClick={() => impersonate(s)}
                    data-testid={`impersonate-${s.id}`}
                    title="Click to view as this staff (Admin Impersonation)"
                    className="text-left text-blue-400 hover:text-blue-300 hover:underline font-semibold cursor-pointer">
                    {s.name}
                  </button>
                  {s.vip && <Badge className="ml-2 bg-amber-500/20 text-amber-400 border-amber-500/30">VIP</Badge>}
                </TableCell>
                <TableCell className="font-mono-alt">{s.phone}</TableCell>
                <TableCell>{s.district}</TableCell>
                <TableCell className="text-xs" data-testid={`bstaff-bank-cell-${s.id}`}>
                  {s.bank_name ? (
                    <div>
                      <div className="text-white font-semibold">{s.bank_name}</div>
                      <div className="text-slate-400">{s.branch_name || "—"}</div>
                    </div>
                  ) : <span className="text-slate-600">—</span>}
                </TableCell>
                <TableCell data-testid={`bstaff-referral-cell-${s.id}`}>
                  <div className={`inline-flex flex-col items-start rounded-md px-2 py-1 border ${s.referral_count > 0 ? "bg-[#FFD700]/10 border-[#FFD700]/30" : "bg-slate-900/50 border-slate-800"}`}>
                    <span className={`font-mono-alt font-bold text-sm ${s.referral_count > 0 ? "text-[#FFD700]" : "text-slate-500"}`}>{fmtShortINR(s.referral_amount || 0)}</span>
                    <span className="text-[10px] uppercase tracking-widest text-slate-400">{s.referral_count || 0} case{(s.referral_count || 0) === 1 ? "" : "s"}</span>
                  </div>
                </TableCell>
                <TableCell data-testid={`bstaff-ok-cell-${s.id}`}>
                  <div className={`inline-flex flex-col items-start rounded-md px-2 py-1 border ${s.ok_count > 0 ? "bg-emerald-500/10 border-emerald-500/30" : "bg-slate-900/50 border-slate-800"}`}>
                    <span className={`font-mono-alt font-bold text-sm ${s.ok_count > 0 ? "text-emerald-300" : "text-slate-500"}`}>{fmtShortINR(s.ok_amount || 0)}</span>
                    <span className="text-[10px] uppercase tracking-widest text-slate-400">{s.ok_count || 0} case{(s.ok_count || 0) === 1 ? "" : "s"}</span>
                  </div>
                </TableCell>
                <TableCell><span className={`font-bold ${s.score>=90?"text-emerald-400":s.score>=70?"text-amber-400":"text-red-400"}`}>{s.score}%</span></TableCell>
                <TableCell>
                  <StatusBadge
                    staff={s}
                    onClick={() => {
                      if (mode === "approval_waiting" && s.pending_profile) setReviewStaff(s);
                      else setStatusStaff(s);
                    }}
                  />
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <button data-testid={`edit-btn-${s.id}`} onClick={() => setEditStaff(s)}
                    title="Edit staff details"
                    className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-slate-700 text-slate-300 hover:text-amber-400 hover:border-amber-500/50 hover:bg-amber-500/10 transition-colors mr-2">
                    <Pencil className="w-3.5 h-3.5"/>
                  </button>
                  <button data-testid={`reset-pw-btn-${s.id}`} onClick={() => setResetStaff(s)}
                    title="Reset password"
                    className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-slate-700 text-slate-300 hover:text-blue-400 hover:border-blue-500/50 hover:bg-blue-500/10 transition-colors">
                    <KeyRound className="w-3.5 h-3.5"/>
                  </button>
                </TableCell>
              </TableRow>
            );
          })}
          {loading && staff.length === 0 && (
            <TableRow><TableCell colSpan={9} className="text-center py-10" data-testid="bstaff-loading">
              <div className="flex items-center justify-center gap-2 text-slate-400"><RefreshCw className="w-4 h-4 animate-spin"/> Loading bank staff…</div>
            </TableCell></TableRow>
          )}
          {!loading && filtered.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-8">No bank staff in this filter</TableCell></TableRow>}
        </TableBody>
      </Table>
      {loaded && total > 0 && (
        <Paginator page={page} pageSize={pageSize} total={total} onPage={setPage} testid="bstaff-pager"/>
      )}
      <StatusDialog staff={statusStaff} endpoint="/admin/bank-staff" open={!!statusStaff} onClose={() => setStatusStaff(null)} onSaved={() => { setStatusStaff(null); load(); }} />
      <ReviewProfileDialog staff={reviewStaff} open={!!reviewStaff} onClose={() => setReviewStaff(null)} onDone={() => { setReviewStaff(null); load(); }} branchName={branchNameById(reviewStaff?.pending_profile?.branch_id)} />
      <AdminEditUserModal staff={editStaff} banks={banks} districts={locDistricts} open={!!editStaff} onClose={() => setEditStaff(null)} onSaved={() => { setEditStaff(null); load(); }} />
      <ResetPasswordDialog staff={resetStaff} open={!!resetStaff} onClose={() => setResetStaff(null)} onSaved={() => setResetStaff(null)} />
    </div>
  );
}

// -- Office Staff -----------------------------------------------
function OfficeStaffPage() {
  const { districts: locDistricts } = useLocations();
  const [staff, setStaff] = useState([]);
  const [statusStaff, setStatusStaff] = useState(null);
  const [editStaff, setEditStaff] = useState(null);
  const [resetStaff, setResetStaff] = useState(null);
  const [f, setF] = useState({ name:"", phone:"", password:"", sub_role:"telecaller", district: "", monthly_salary: "" });
  const [open, setOpen] = useState(false);
  const [targetOpen, setTargetOpen] = useState(false);
  const [tf, setTF] = useState({ staff_id: "", month: new Date().getMonth()+1, year: new Date().getFullYear(), target_cases: "", target_amount_lakhs: "", incentive: "" });
  const [filters, setFilters] = useState({ role: "all", district: "all", target_status: "all" });
  const currentMonth = new Date().toISOString().slice(0,7);
  const impersonate = async (s) => {
    try {
      const { data } = await api.post(`/admin/impersonate/${s.id}`);
      beginImpersonation({ token: data.token, user: data.user });
      toast.success(`Viewing as ${s.name}`);
      const map = { bank_staff: "/bank", office_staff: "/staff", partner: "/partner" };
      window.location.href = map[s.role] || "/";
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("month", currentMonth);
      params.set("page", String(page));
      params.set("page_size", String(pageSize));
      if (debouncedSearch) params.set("q", debouncedSearch);
      if (filters.role !== "all") params.set("sub_role", filters.role);
      const r = await api.get(`/admin/office-staff?${params.toString()}`);
      const payload = r.data && typeof r.data === "object" && Array.isArray(r.data.items) ? r.data : { items: r.data || [], total: (r.data || []).length };
      setStaff(payload.items);
      setTotal(payload.total || 0);
      setLoaded(true);
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, debouncedSearch, filters.role]);
  useRefocus(load);
  useEffect(() => { setPage(1); }, [debouncedSearch, filters.role]);
  const create = async () => {
    if (!f.name || f.phone.length !== 10 || !f.password) return toast.error("Please fill name, phone and password");
    if (!f.district) return toast.error("Please select a district");
    if (!f.monthly_salary || Number(f.monthly_salary) <= 0) return toast.error("Please enter monthly salary");
    try{
      await api.post("/admin/office-staff", { ...f, monthly_salary: Number(f.monthly_salary) });
      toast.success("Office staff added");
      setOpen(false);
      setF({name:"",phone:"",password:"",sub_role:"telecaller", district:"", monthly_salary:""});
      load();
    }catch(e){toast.error(errorMessage(e));}
  };
  const saveTarget = async () => {
    if (!tf.staff_id) return toast.error("Please select a staff");
    if (!tf.target_cases || Number(tf.target_cases) <= 0) return toast.error("Enter target cases");
    if (!tf.target_amount_lakhs || Number(tf.target_amount_lakhs) <= 0) return toast.error("Enter target amount in lakhs");
    try {
      await api.post("/admin/targets", {
        staff_id: tf.staff_id, month: Number(tf.month), year: Number(tf.year),
        target_cases: Number(tf.target_cases), target_amount_lakhs: Number(tf.target_amount_lakhs), incentive: Number(tf.incentive || 0),
      });
      toast.success("Target saved");
      setTargetOpen(false);
      setTF({ staff_id: "", month: new Date().getMonth()+1, year: new Date().getFullYear(), target_cases: "", target_amount_lakhs: "", incentive: "" });
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const roleLabel = (s) => ({ telecaller: "Telecaller", field: "Field", district_manager: "District Manager" }[s?.sub_role] || (s?.sub_role || ""));
  const districtsList = Array.from(new Set(staff.map(s => s.district || s.managed_district).filter(Boolean)));

  const pctFor = (s) => {
    if (!s.target || !s.target.target_cases) return null;
    const casePct = Math.min(100, ((s.achieved?.cases || 0) / s.target.target_cases) * 100);
    return Math.round(casePct);
  };
  const targetStatusOf = (s) => {
    const p = pctFor(s);
    if (p === null) return "no_target";
    if (p >= 100) return "achieved";
    if (p >= 50) return "in_progress";
    return "not_achieved";
  };
  const filtered = staff.filter(s => {
    if (filters.role !== "all" && s.sub_role !== filters.role) return false;
    const d = s.district || s.managed_district || "";
    if (filters.district !== "all" && d !== filters.district) return false;
    if (filters.target_status !== "all" && targetStatusOf(s) !== filters.target_status) return false;
    return true;
  });

  return (
    <div className="card-surface p-6">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div><div className="font-heading text-xl text-white">Office Staff</div><div className="text-sm text-slate-500">Telecaller · Field · District Manager. Targets for {currentMonth}.</div></div>
        <div className="flex gap-2">
          <Dialog open={targetOpen} onOpenChange={setTargetOpen}>
            <DialogTrigger asChild><Button data-testid="add-target-btn" variant="secondary" className="bg-slate-800 hover:bg-slate-700 text-white rounded-full"><Target className="w-4 h-4 mr-2"/>Add Target</Button></DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800 text-white">
              <DialogHeader><DialogTitle>Add Monthly Target</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-slate-400 text-xs uppercase tracking-widest">Staff</Label>
                  <Select value={tf.staff_id} onValueChange={v => setTF({...tf, staff_id: v})}>
                    <SelectTrigger data-testid="target-staff" className="bg-slate-950 border-slate-700 mt-2 w-full"><SelectValue placeholder="Select staff"/></SelectTrigger>
                    <SelectContent className="bg-slate-900 text-white border-slate-700 max-h-64 min-w-[--radix-select-trigger-width]">
                      {staff.map(s => <SelectItem key={s.id} value={s.id}>{s.name} · {roleLabel(s)} · {s.district}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-400 text-xs uppercase tracking-widest">Month</Label>
                    <Select value={String(tf.month)} onValueChange={v => setTF({...tf, month: Number(v)})}>
                      <SelectTrigger data-testid="target-month" className="bg-slate-950 border-slate-700 mt-2"><SelectValue/></SelectTrigger>
                      <SelectContent className="bg-slate-900 text-white border-slate-700">
                        {["January","February","March","April","May","June","July","August","September","October","November","December"].map((m, i) => (
                          <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-400 text-xs uppercase tracking-widest">Year</Label>
                    <Input data-testid="target-year" type="number" value={tf.year} onChange={e => setTF({...tf, year: Number(e.target.value)})} className="bg-slate-950 border-slate-700 mt-2"/>
                  </div>
                </div>
                <div>
                  <Label className="text-slate-400 text-xs uppercase tracking-widest">Target Cases</Label>
                  <Input data-testid="target-cases" type="number" placeholder="e.g. 30" value={tf.target_cases} onChange={e => setTF({...tf, target_cases: e.target.value.replace(/\D/g,'')})} className="bg-slate-950 border-slate-700 mt-2"/>
                </div>
                <div>
                  <Label className="text-slate-400 text-xs uppercase tracking-widest">Target Disbursement (Lakhs)</Label>
                  <Input data-testid="target-amount" type="number" placeholder="e.g. 50" value={tf.target_amount_lakhs} onChange={e => setTF({...tf, target_amount_lakhs: e.target.value})} className="bg-slate-950 border-slate-700 mt-2"/>
                </div>
                <div>
                  <Label className="text-slate-400 text-xs uppercase tracking-widest">Incentive if Achieved (₹)</Label>
                  <Input data-testid="target-incentive" type="number" placeholder="e.g. 5000" value={tf.incentive} onChange={e => setTF({...tf, incentive: e.target.value.replace(/\D/g,'')})} className="bg-slate-950 border-slate-700 mt-2"/>
                </div>
                <Button data-testid="target-save" onClick={saveTarget} className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full">Save Target</Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-office-staff-btn" className="bg-amber-500 hover:bg-amber-400 text-slate-900 rounded-full"><Plus className="w-4 h-4 mr-2"/>Add Staff</Button></DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800 text-white">
              <DialogHeader><DialogTitle>New Office Staff</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Input data-testid="ostaff-name" placeholder="Name" value={f.name} onChange={e=>setF({...f,name:e.target.value})} className="bg-slate-950 border-slate-700"/>
                <Input data-testid="ostaff-phone" placeholder="Phone (Login ID)" value={f.phone} maxLength={10} onChange={e=>setF({...f,phone:e.target.value.replace(/\D/g,'')})} className="bg-slate-950 border-slate-700"/>
                <Input data-testid="ostaff-password" placeholder="Set password" value={f.password} onChange={e=>setF({...f,password:e.target.value})} className="bg-slate-950 border-slate-700"/>
                <Select value={f.sub_role} onValueChange={v=>setF({...f, sub_role:v})}>
                  <SelectTrigger data-testid="ostaff-role" className="bg-slate-950 border-slate-700"><SelectValue/></SelectTrigger>
                  <SelectContent className="bg-slate-900 text-white border-slate-700">
                    <SelectItem value="telecaller">Telecaller</SelectItem>
                    <SelectItem value="field">Field</SelectItem>
                    <SelectItem value="district_manager">District Manager</SelectItem>
                  </SelectContent>
                </Select>
                <div>
                  <Label className="text-slate-400 text-xs uppercase tracking-widest">Assigned District <span className="text-red-400">*</span></Label>
                  <Select value={f.district} onValueChange={v=>setF({...f, district: v})}>
                    <SelectTrigger data-testid="ostaff-district" className="bg-slate-950 border-slate-700 mt-2 w-full"><SelectValue placeholder="Select district"/></SelectTrigger>
                    <SelectContent className="bg-slate-900 text-white border-slate-700 max-h-64 min-w-[--radix-select-trigger-width]">
                      {locDistricts.length === 0 ? <div className="p-3 text-sm text-slate-400">No districts configured. Go to Location Master.</div> : locDistricts.map(d => <SelectItem key={d.id} value={d.name} className="whitespace-nowrap">{d.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-400 text-xs uppercase tracking-widest">Monthly Salary (₹) <span className="text-red-400">*</span></Label>
                  <div className="relative mt-2">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">₹</span>
                    <Input data-testid="ostaff-salary" type="number" placeholder="e.g. 15000" value={f.monthly_salary} onChange={e=>setF({...f, monthly_salary: e.target.value.replace(/\D/g,'')})} className="bg-slate-950 border-slate-700 pl-8"/>
                  </div>
                </div>
                <Button data-testid="ostaff-save" onClick={create} className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full">Save</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6" data-testid="office-filters">
        <Select value={filters.role} onValueChange={v => setFilters({...filters, role: v})}>
          <SelectTrigger data-testid="filter-role" className="bg-slate-950 border-slate-700"><SelectValue/></SelectTrigger>
          <SelectContent className="bg-slate-900 text-white border-slate-700">
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="telecaller">Telecaller</SelectItem>
            <SelectItem value="field">Field Staff</SelectItem>
            <SelectItem value="district_manager">District Manager</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.district} onValueChange={v => setFilters({...filters, district: v})}>
          <SelectTrigger data-testid="filter-district" className="bg-slate-950 border-slate-700"><SelectValue/></SelectTrigger>
          <SelectContent className="bg-slate-900 text-white border-slate-700 max-h-64">
            <SelectItem value="all">All Districts</SelectItem>
            {locDistricts.map(d => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.target_status} onValueChange={v => setFilters({...filters, target_status: v})}>
          <SelectTrigger data-testid="filter-target-status" className="bg-slate-950 border-slate-700"><SelectValue/></SelectTrigger>
          <SelectContent className="bg-slate-900 text-white border-slate-700">
            <SelectItem value="all">All Target Status</SelectItem>
            <SelectItem value="achieved">Achieved (100%+)</SelectItem>
            <SelectItem value="in_progress">In Progress (50-99%)</SelectItem>
            <SelectItem value="not_achieved">Not Achieved (&lt;50%)</SelectItem>
            <SelectItem value="no_target">No Target Set</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto -mx-6 md:-mx-6 px-6 md:px-6 pb-2 rounded-lg" data-testid="office-staff-table-scroll">
        <div className="min-w-[1100px]">
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-slate-950/95 backdrop-blur">
              <TableRow className="border-white/5">
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>District</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Achieved</TableHead>
                <TableHead>%</TableHead>
                <TableHead>Salary</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(s=>{
                const t = s.target; const a = s.achieved || { cases: 0, amount_lakhs: 0 };
                const pct = pctFor(s);
                const pctColor = pct === null ? "text-slate-500" : pct >= 80 ? "text-emerald-400" : pct >= 50 ? "text-amber-400" : "text-red-400";
                const barColor = pct === null ? "bg-slate-700" : pct >= 80 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-red-500";
                return (
                  <TableRow key={s.id} className="border-white/5" data-testid={`ostaff-row-${s.id}`}>
                    <TableCell className="text-white whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => impersonate(s)}
                        data-testid={`impersonate-${s.id}`}
                        title="Click to view as this staff (Admin Impersonation)"
                        className="text-left text-blue-400 hover:text-blue-300 hover:underline font-semibold cursor-pointer">
                        {s.name}
                      </button>
                      <div className="text-xs text-slate-500 font-mono-alt">{s.phone}</div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <Badge className={s.sub_role === "district_manager" ? "bg-amber-500/20 text-amber-400 border-amber-500/30" : "bg-slate-800 text-slate-300"}>{roleLabel(s)}</Badge>
                    </TableCell>
                    <TableCell className="text-slate-300 text-sm whitespace-nowrap">{s.district || s.managed_district || <span className="text-slate-600">—</span>}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">
                      <InlineEditCell
                        value={t?.target_cases ?? ""}
                        type="number"
                        testid={`ostaff-target-${s.id}`}
                        onSave={async (v) => {
                          try {
                            const [y, mo] = currentMonth.split("-");
                            await api.post("/admin/targets", {
                              staff_id: s.id,
                              month: Number(mo),
                              year: Number(y),
                              target_cases: Number(v || 0),
                              target_amount_lakhs: Number(t?.target_amount_lakhs || 0),
                              incentive: Number(t?.incentive || 0),
                            });
                            toast.success("Target updated");
                            load();
                          } catch (e) { toast.error(errorMessage(e)); throw e; }
                        }}
                        render={() => t
                          ? <div><div className="text-white">{t.target_cases} cases</div><div className="text-xs text-slate-400">₹{t.target_amount_lakhs}L</div>{t.incentive ? <div className="text-xs text-amber-400">₹{Number(t.incentive).toLocaleString("en-IN")} bonus</div> : null}</div>
                          : <span className="text-slate-600">— dbl-click</span>}
                      />
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap">
                      <div className="text-white">{a.cases} cases</div>
                      <div className="text-xs text-slate-400">₹{a.amount_lakhs}L</div>
                    </TableCell>
                    <TableCell>
                      {pct === null ? <span className="text-slate-600">—</span> : (
                        <div className="flex items-center gap-2" data-testid={`ostaff-pct-${s.id}`}>
                          <div className="relative w-9 h-9">
                            <svg viewBox="0 0 36 36" className="w-9 h-9 -rotate-90">
                              <circle cx="18" cy="18" r="15.9" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3"/>
                              <circle cx="18" cy="18" r="15.9" fill="none" strokeLinecap="round"
                                className={barColor.replace("bg-","stroke-")} strokeWidth="3"
                                strokeDasharray={`${(pct/100)*100} 100`}/>
                            </svg>
                            <div className={`absolute inset-0 flex items-center justify-center text-[10px] font-bold ${pctColor}`}>{pct}%</div>
                          </div>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-mono-alt text-sm text-slate-300 whitespace-nowrap">
                      <InlineEditCell
                        value={s.monthly_salary || ""}
                        type="number"
                        testid={`ostaff-salary-${s.id}`}
                        onSave={async (v) => {
                          try {
                            await api.put(`/admin/users/${s.id}`, { monthly_salary: Number(v || 0) });
                            toast.success("Salary updated");
                            load();
                          } catch (e) { toast.error(errorMessage(e)); throw e; }
                        }}
                        render={(v) => v ? `₹${Number(v).toLocaleString("en-IN")}` : <span className="text-slate-600">— dbl-click</span>}
                      />
                    </TableCell>
                    <TableCell className="whitespace-nowrap"><StatusBadge staff={s} onClick={() => setStatusStaff(s)} /></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <button data-testid={`edit-btn-${s.id}`} onClick={() => setEditStaff(s)}
                        title="Edit staff details"
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-slate-700 text-slate-300 hover:text-amber-400 hover:border-amber-500/50 hover:bg-amber-500/10 transition-colors mr-2">
                        <Pencil className="w-3.5 h-3.5"/>
                      </button>
                      <button data-testid={`reset-pw-btn-${s.id}`} onClick={() => setResetStaff(s)}
                        title="Reset password"
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-slate-700 text-slate-300 hover:text-blue-400 hover:border-blue-500/50 hover:bg-blue-500/10 transition-colors">
                        <KeyRound className="w-3.5 h-3.5"/>
                      </button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {loaded && filtered.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-8">No staff match the current filters</TableCell></TableRow>}
              {!loaded && loading && <TableRow><TableCell colSpan={9} className="py-2"><LoadingSkeleton rows={5} label="Loading office staff…" testid="office-loading"/></TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>
      {loaded && total > 0 && (
        <Paginator page={page} pageSize={pageSize} total={total} onPage={setPage} testid="office-pager"/>
      )}
      <div className="md:hidden text-[10px] text-slate-500 mt-2 text-center">← Swipe horizontally to see all columns →</div>
      <StatusDialog staff={statusStaff} endpoint="/admin/office-staff" open={!!statusStaff} onClose={() => setStatusStaff(null)} onSaved={() => { setStatusStaff(null); load(); }} />
      <AdminEditUserModal staff={editStaff} banks={[]} districts={locDistricts} open={!!editStaff} onClose={() => setEditStaff(null)} onSaved={() => { setEditStaff(null); load(); }} />
      <ResetPasswordDialog staff={resetStaff} open={!!resetStaff} onClose={() => setResetStaff(null)} onSaved={() => setResetStaff(null)} />
    </div>
  );
}

// -- Status Audit Log -----------------------------------------------
function AuditLogPage() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/status-audit").then(r => setRows(r.data)).catch(e => toast.error(errorMessage(e))); }, []);
  const color = (s) => s === "terminated" ? "bg-red-500/20 text-red-400 border-red-500/30" : s === "suspended" ? "bg-amber-500/20 text-amber-400 border-amber-500/30" : "bg-emerald-500/20 text-emerald-400 border-emerald-500/30";
  const exportCsv = () => {
    if (rows.length === 0) return toast.error("No entries to export");
    const escape = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const header = ["When","Staff Name","Staff Phone","Staff Role","Action","Duration (days)","Actor","Reason"];
    const lines = [header.join(",")].concat(rows.map(r => [
      new Date(r.at).toISOString(), r.staff_name, r.staff_phone, r.staff_role, r.new_status, r.days ?? "", r.actor_name, r.reason
    ].map(escape).join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `audit_log_${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded");
  };
  return (
    <div className="card-surface p-6" data-testid="audit-page">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="font-heading text-xl text-white">Status Audit Log</div>
          <div className="text-sm text-slate-500">Full audit trail of who suspended or terminated whom, when, and why.</div>
        </div>
        <Button onClick={exportCsv} data-testid="audit-export-csv" className="bg-slate-800 hover:bg-slate-700 text-white rounded-full">
          <Download className="w-4 h-4 mr-2"/>Export CSV
        </Button>
      </div>
      <Table>
        <TableHeader><TableRow className="border-white/5"><TableHead>When</TableHead><TableHead>Staff</TableHead><TableHead>Role</TableHead><TableHead>Action</TableHead><TableHead>Duration</TableHead><TableHead>By</TableHead><TableHead>Reason</TableHead></TableRow></TableHeader>
        <TableBody>
          {rows.map(r => (
            <TableRow key={r.id} className="border-white/5" data-testid={`audit-row-${r.id}`}>
              <TableCell className="text-slate-400 whitespace-nowrap text-sm font-mono-alt">{new Date(r.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</TableCell>
              <TableCell className="text-white">{r.staff_name}<div className="text-xs text-slate-500 font-mono-alt">{r.staff_phone}</div></TableCell>
              <TableCell className="text-slate-400 text-sm">{(r.staff_role||"").replace("_"," ")}</TableCell>
              <TableCell><Badge className={color(r.new_status)}>{r.new_status}</Badge></TableCell>
              <TableCell className="text-slate-400 text-sm">{r.days ? `${r.days} days` : "—"}</TableCell>
              <TableCell className="text-slate-300 text-sm">{r.actor_name}</TableCell>
              <TableCell className="text-slate-400 text-sm max-w-xs">{r.reason || "—"}</TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">No audit entries yet</TableCell></TableRow>}
        </TableBody>
      </Table>
    </div>
  );
}

// -- Commission Report -----------------------------------------------
function CommissionReportPage() {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const load = () => api.get(`/admin/commission-report?month=${month}`).then(r => setData(r.data)).catch(e => toast.error(errorMessage(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);
  const inr = (n) => `₹${Math.round(n||0).toLocaleString("en-IN")}`;
  const chartData = (data?.banks || []).map(b => ({ name: b.bank_name, commission: Math.round(b.commission_earned) }));
  return (
    <div className="space-y-6" data-testid="commission-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="font-heading text-xl text-white">Commission Report</div>
          <div className="text-sm text-slate-500">Per-bank commission Shakthi earned in the selected month.</div>
        </div>
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="commission-month" className="bg-slate-950 border-slate-700 max-w-[180px]" />
      </div>
      <div className="grid md:grid-cols-4 gap-6">
        <div className="card-surface p-6" data-testid="stat-total-commission">
          <IndianRupee className="w-5 h-5 text-amber-500 mb-3"/>
          <div className="font-display text-3xl text-white">{inr(data?.total_commission)}</div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mt-2">Total Commission</div>
        </div>
        <div className="card-surface p-6">
          <TrendingUp className="w-5 h-5 text-amber-500 mb-3"/>
          <div className="font-display text-3xl text-white">{inr(data?.total_disbursed)}</div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mt-2">Total Disbursed</div>
        </div>
        <div className="card-surface p-6">
          <ListFilter className="w-5 h-5 text-amber-500 mb-3"/>
          <div className="font-display text-3xl text-white">{data?.cases_count ?? 0}</div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mt-2">Cases</div>
        </div>
        <div className="card-surface p-6">
          <Crown className="w-5 h-5 text-amber-500 mb-3"/>
          <div className="font-heading text-xl text-white truncate">{data?.top_bank || "—"}</div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mt-2">Top Bank This Month</div>
        </div>
      </div>
      <div className="card-surface p-6">
        <div className="font-heading text-white mb-4">Commission by Bank</div>
        {chartData.length === 0 ? (
          <div className="text-slate-500 text-center py-12">No disbursed cases in this month yet.</div>
        ) : (
          <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 20, right: 20, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)"/>
                <XAxis dataKey="name" stroke="#94A3B8" fontSize={12}/>
                <YAxis stroke="#94A3B8" fontSize={12} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`}/>
                <RTooltip contentStyle={{ background: "#0F172A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} labelStyle={{ color: "#F8FAFC" }} formatter={(v) => inr(v)}/>
                <Bar dataKey="commission" fill="#F59E0B" radius={[8, 8, 0, 0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      <div className="card-surface p-6">
        <Table>
          <TableHeader><TableRow className="border-white/5"><TableHead>Bank</TableHead><TableHead>Cases</TableHead><TableHead>Disbursed</TableHead><TableHead>Commission Earned</TableHead></TableRow></TableHeader>
          <TableBody>
            {(data?.banks || []).map(b => (
              <TableRow key={b.bank_id || b.bank_name} className="border-white/5" data-testid={`commission-row-${b.bank_id}`}>
                <TableCell className="text-white">{b.bank_name}</TableCell>
                <TableCell className="text-slate-300">{b.cases}</TableCell>
                <TableCell className="text-slate-300">{inr(b.disbursed_amount)}</TableCell>
                <TableCell className="text-amber-400 font-bold">{inr(b.commission_earned)}</TableCell>
              </TableRow>
            ))}
            {(!data || data.banks.length === 0) && <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-8">No commission earned yet in this month</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// -- Upload page extracted to /pages/admin/UploadLeadsPage.jsx --

// -- All Leads -----------------------------------------------
function AllLeadsPage() {
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [sourceQ, setSourceQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [assignFilter, setAssignFilter] = useState("all"); // all | unassigned | <staff_id>
  const [telecallers, setTelecallers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [assignTo, setAssignTo] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [overwriteList, setOverwriteList] = useState([]); // [{lead_id, name, currentName}]
  const [historyLead, setHistoryLead] = useState(null); // {lead_id, name, phone, history}
  const [exportOpen, setExportOpen] = useState(false);
  const [bulkVerifyOpen, setBulkVerifyOpen] = useState(false);
  const [bvLoan, setBvLoan] = useState("");
  const [bvDistrict, setBvDistrict] = useState("");
  const [bvAmount, setBvAmount] = useState("");
  const [bvSaving, setBvSaving] = useState(false);
  const [loans, setLoans] = useState([]);
  const { districts: bvDistricts } = useLocations();
  useEffect(() => {
    api.get("/loans").then(r => setLoans(r.data || [])).catch(() => {});
  }, []);
  const doBulkVerify = async () => {
    if (!bvLoan && !bvDistrict && !bvAmount) return toast.error("Set at least one field");
    setBvSaving(true);
    try {
      const body = { lead_ids: Array.from(selected) };
      if (bvLoan) body.loan_type = bvLoan;
      if (bvDistrict) body.district = bvDistrict;
      if (bvAmount) body.amount = Number(bvAmount);
      const r = await api.post("/admin/leads/bulk-verify", body);
      toast.success(`Verified ${r.data.updated}/${r.data.requested} lead(s)`);
      setBulkVerifyOpen(false);
      setBvLoan(""); setBvDistrict(""); setBvAmount("");
      setSelected(new Set());
      await loadLeads();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setBvSaving(false); }
  };
  const [exportCols, setExportCols] = useState({
    lead_id: true, name: true, phone: true, loan_type: false, amount: false,
    status: false, assigned_office_staff_name: false, at: true, event: true,
  });

  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ((q || "").trim()), 350);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => { setPage(1); }, [debouncedQ]);
  const [statusTab, setStatusTab] = useState("all"); // all | rejected
  useEffect(() => { setPage(1); }, [debouncedQ, statusTab]);
  const loadLeads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("page_size", String(pageSize));
      if (debouncedQ) params.set("q", debouncedQ);
      if (statusTab === "rejected") params.set("status", "rejected");
      const r = await api.get(`/admin/leads?${params.toString()}`);
      const payload = r.data && typeof r.data === "object" && Array.isArray(r.data.items) ? r.data : { items: r.data || [], total: (r.data || []).length };
      setLeads(payload.items);
      setTotal(payload.total || 0);
      setLoaded(true);
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadLeads(); /* eslint-disable-next-line */ }, [page, debouncedQ, statusTab]);
  useRefocus(loadLeads);
  useEffect(() => {
    api.get("/admin/office-staff")
      .then(r => setTelecallers((r.data || []).filter(s => s.sub_role === "telecaller" && s.status !== "terminated")))
      .catch(() => {});
  }, []);

  const toggleOne = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleAll = (rows) => {
    setSelected(prev => {
      const allIds = rows.map(r => r.lead_id);
      const allOn = allIds.every(id => prev.has(id));
      if (allOn) return new Set();
      return new Set(allIds);
    });
  };

  const doAssign = async () => {
    if (!assignTo) return toast.error("Choose a telecaller first");
    if (selected.size === 0) return toast.error("Select at least one lead");
    // Reassign guard — flag any lead already with a DIFFERENT telecaller
    const conflicts = leads
      .filter(l => selected.has(l.lead_id))
      .filter(l => l.assigned_office_staff && l.assigned_office_staff !== assignTo)
      .map(l => ({
        lead_id: l.lead_id,
        name: l.name,
        currentName: l.assigned_office_staff_name || "another telecaller",
      }));
    if (conflicts.length > 0) {
      setOverwriteList(conflicts);
      setConfirmOpen(true);
      return;
    }
    await executeAssign();
  };

  const openHistory = async (lead_id) => {
    try {
      const r = await api.get(`/admin/leads/${lead_id}/history`);
      setHistoryLead(r.data);
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const exportBulkHistory = async (columnsOverride = null) => {
    const ids = filtered.map(l => l.lead_id);
    if (ids.length === 0) return toast.error("No leads to export");
    if (ids.length > 500) return toast.error("Filter down to under 500 leads first");
    try {
      const r = await api.post("/admin/leads/bulk-history", { lead_ids: ids });
      const allCols = [
        { key: "lead_id",  label: "Lead ID" },
        { key: "name",     label: "Name" },
        { key: "phone",    label: "Phone" },
        { key: "loan_type",label: "Loan" },
        { key: "amount",   label: "Amount" },
        { key: "status",   label: "Status" },
        { key: "assigned_office_staff_name", label: "Assigned To" },
        { key: "at",       label: "Timestamp" },
        { key: "event",    label: "Event" },
      ];
      const active = columnsOverride || exportCols;
      const cols = allCols.filter(c => active[c.key]);
      if (cols.length === 0) return toast.error("Pick at least one column");
      const esc = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g,'""')}"` : s; };
      const rows = [cols.map(c => c.label)];
      for (const l of (r.data.leads || [])) {
        for (const h of (l.history || [])) {
          const merged = { ...l, at: h.at, event: h.event };
          rows.push(cols.map(c => merged[c.key] ?? ""));
        }
      }
      if (rows.length === 1) return toast.error("No history entries in this selection");
      const csv = rows.map(r => r.map(esc).join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0,10);
      a.href = url; a.download = `leads_history_${stamp}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${r.data.leads.length} lead(s) · ${cols.length} column(s)`);
      setExportOpen(false);
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const executeAssign = async () => {
    setConfirmOpen(false);
    setAssigning(true);
    try {
      const res = await api.post("/admin/leads/bulk-assign", {
        lead_ids: Array.from(selected),
        staff_id: assignTo,
      });
      toast.success(`Assigned ${res.data.assigned} lead(s) to ${res.data.staff_name}`);
      setSelected(new Set());
      setAssignTo("");
      setOverwriteList([]);
      await loadLeads();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setAssigning(false);
    }
  };

  // Map source → new 3-value taxonomy (Direct / Website / Referral)
  const genericType = (l) => {
    const s = (l.source || "").toLowerCase();
    if (s === "website") return "website";
    if (s === "referral" || l.pool_id) return "referral";
    return "direct";
  };
  const typeColor = {
    website: "bg-purple-500/20 text-purple-300 border-purple-500/40",
    referral: "bg-amber-500/20 text-amber-300 border-amber-500/40",
    direct: "bg-slate-800 text-slate-300 border-slate-700",
  };
  const typeDisplay = (l) => {
    const g = genericType(l);
    if (g === "referral") return `Referral - ${l.original_pool_source_name || l.source_name || "unknown"}`;
    return g.charAt(0).toUpperCase() + g.slice(1);
  };
  const roleLabel = (r) => ({
    admin: "Admin", bank_staff: "Bank Staff", telecaller: "Telecaller",
    field_staff: "Field Staff", mahalir_manager: "Mahalir Kuzhu Manager",
    office_staff: "Office Staff", customer: "Customer",
  }[r] || (r || "—"));
  const uploaderName = (l) => (
    l.referred_by_staff_name ||
    l.referred_by_customer_name ||
    (l.source_name && l.source_name.split(" - ")[1]) ||
    "—"
  );
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return iso; }
  };

  const filtered = leads.filter(l => {
    const gq = q.toLowerCase();
    const matchQ = !gq || l.phone?.includes(q) || (l.name||"").toLowerCase().includes(gq) ||
                   (l.lead_id||"").toLowerCase().includes(gq) || (l.source_name||"").toLowerCase().includes(gq);
    const matchSrcName = !sourceQ || (l.source_name || "").toLowerCase().includes(sourceQ.toLowerCase());
    const matchType = typeFilter === "all" || genericType(l) === typeFilter;
    let matchAssign = true;
    if (assignFilter === "unassigned") matchAssign = !l.assigned_office_staff;
    else if (assignFilter === "assigned") matchAssign = !!l.assigned_office_staff;
    else if (assignFilter !== "all") matchAssign = l.assigned_office_staff === assignFilter;
    return matchQ && matchSrcName && matchType && matchAssign;
  });

  const statusColor = (s) => ({pending_verification:"bg-slate-700",assigned_to_office:"bg-blue-500/20 text-blue-400 border-blue-500/30",in_bank_pool:"bg-purple-500/20 text-purple-400 border-purple-500/30",taken:"bg-amber-500/20 text-amber-400 border-amber-500/30",ok:"bg-emerald-500/20 text-emerald-400 border-emerald-500/30",disbursed:"bg-emerald-500/40 text-emerald-300 border-emerald-500/40",new_number_only:"bg-slate-700 text-slate-300 border-slate-600"}[s] || "bg-slate-700");

  const totalCount = leads.length;
  const assignedCount = leads.filter(l => l.assigned_office_staff).length;
  const unassignedCount = totalCount - assignedCount;
  const visible = filtered.slice(0, 100);
  const allSelectedOnPage = visible.length > 0 && visible.every(l => selected.has(l.lead_id));

  return (
    <div className="card-surface p-6">
      <DailyFuelMeter/>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="font-heading text-xl text-white">All Leads</div>
          <div className="text-sm text-slate-500">{filtered.length} shown</div>
          <div className="mt-2 inline-flex rounded-full border border-slate-700 bg-slate-900 p-0.5 text-xs" data-testid="allleads-status-tabs">
            <button type="button" onClick={() => setStatusTab("all")} data-testid="tab-all"
              className={`px-3 py-1 rounded-full font-bold uppercase tracking-widest ${statusTab === "all" ? "bg-amber-500 text-slate-950" : "text-slate-300 hover:text-white"}`}>All</button>
            <button type="button" onClick={() => setStatusTab("rejected")} data-testid="tab-rejected"
              className={`px-3 py-1 rounded-full font-bold uppercase tracking-widest flex items-center gap-1 ${statusTab === "rejected" ? "bg-red-500 text-white" : "text-red-300 hover:text-red-200"}`}>
              <Ban className="w-3 h-3"/> Rejected
            </button>
          </div>
        </div>
        <div className="flex flex-wrap lg:flex-nowrap gap-2 items-center">
          <Input data-testid="leads-search" placeholder="Search by name / phone / ID" value={q} onChange={e=>setQ(e.target.value)} className="bg-slate-950 border-slate-700 max-w-xs lg:w-52 lg:max-w-none"/>
          <Input data-testid="leads-source-search" placeholder="Enter Source Name" value={sourceQ} onChange={e=>setSourceQ(e.target.value)} className="bg-slate-950 border-slate-700 max-w-xs lg:w-40 lg:max-w-none"/>
          <select data-testid="leads-type-filter" value={typeFilter} onChange={e=>setTypeFilter(e.target.value)} className="bg-slate-950 border border-slate-700 text-white rounded-md px-3 text-sm h-9">
            <option value="all">All Sources</option>
            <option value="direct">Direct</option>
            <option value="website">Website</option>
            <option value="referral">Referral (from pool)</option>
          </select>
          <select data-testid="leads-assign-filter" value={assignFilter} onChange={e=>setAssignFilter(e.target.value)} className="bg-slate-950 border border-slate-700 text-white rounded-md px-3 text-sm h-9 min-w-[160px]">
            <option value="all">All Assignments</option>
            <option value="assigned">Assigned (any)</option>
            <option value="unassigned">Unassigned only</option>
            <optgroup label="Assigned To">
              {telecallers.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </optgroup>
          </select>
          <Button
            data-testid="bulk-history-export-btn"
            onClick={() => setExportOpen(true)}
            disabled={filtered.length === 0}
            variant="ghost"
            className="text-amber-400 hover:bg-amber-500/10 border border-amber-500/30 rounded-full text-xs shrink-0"
            title="Export activity history for all visible leads"
          >
            <Download className="w-3 h-3 mr-2"/> Export History
          </Button>
        </div>
      </div>

      {/* Count widgets */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <button type="button" onClick={() => setAssignFilter("all")} className={`text-left bg-slate-950/60 border rounded-lg p-3 transition-colors ${assignFilter === "all" ? "border-amber-500/40 ring-1 ring-amber-500/30" : "border-white/5 hover:border-white/20"}`} data-testid="widget-total">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Total Leads</div>
          <div className="font-display text-2xl text-white mt-1">{totalCount}</div>
        </button>
        <button type="button" onClick={() => setAssignFilter("assigned")} className={`text-left bg-slate-950/60 border rounded-lg p-3 transition-colors ${assignFilter === "assigned" ? "border-emerald-500/60 ring-1 ring-emerald-500/40" : "border-emerald-500/20 hover:border-emerald-500/50"}`} data-testid="widget-assigned">
          <div className="text-[10px] uppercase tracking-widest text-emerald-400">Assigned</div>
          <div className="font-display text-2xl text-emerald-300 mt-1">{assignedCount}</div>
        </button>
        <button type="button" onClick={() => setAssignFilter("unassigned")} className={`text-left bg-slate-950/60 border rounded-lg p-3 transition-colors ${assignFilter === "unassigned" ? "border-amber-500/60 ring-1 ring-amber-500/40" : "border-amber-500/20 hover:border-amber-500/50"}`} data-testid="widget-unassigned">
          <div className="text-[10px] uppercase tracking-widest text-amber-400">Unassigned</div>
          <div className="font-display text-2xl text-amber-300 mt-1">{unassignedCount}</div>
        </button>
      </div>

      {/* Assign top bar */}
      <div className="flex items-center gap-3 flex-wrap mb-4 p-3 bg-slate-950/40 border border-white/5 rounded-lg">
        <div className="text-sm text-slate-400" data-testid="selection-count">{selected.size} selected</div>
        <select
          data-testid="assign-to-select"
          value={assignTo}
          onChange={e => setAssignTo(e.target.value)}
          className="bg-slate-950 border border-slate-700 text-white rounded-md px-3 py-2 text-sm min-w-[220px]"
        >
          <option value="">Assign To — choose telecaller…</option>
          {telecallers.map(t => (
            <option key={t.id} value={t.id}>{t.name} · {t.phone}</option>
          ))}
        </select>
        <Button
          data-testid="assign-btn"
          onClick={doAssign}
          disabled={assigning || selected.size === 0 || !assignTo}
          className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full disabled:opacity-40"
        >
          <UserPlus className="w-4 h-4 mr-2"/>{assigning ? "Assigning…" : "Assign"}
        </Button>
        <Button
          data-testid="bulk-verify-btn"
          onClick={() => setBulkVerifyOpen(true)}
          disabled={selected.size === 0}
          variant="ghost"
          className="text-emerald-300 hover:bg-emerald-500/10 border border-emerald-500/40 rounded-full disabled:opacity-40"
        >
          <CheckCircle2 className="w-4 h-4 mr-2"/>Bulk Verify ({selected.size})
        </Button>
        {selected.size > 0 && (
          <Button
            data-testid="clear-selection-btn"
            variant="ghost"
            onClick={() => setSelected(new Set())}
            className="text-slate-400 hover:text-white text-xs"
          >Clear</Button>
        )}
      </div>

      <div className="overflow-x-auto lg:overflow-x-visible -mx-6 md:-mx-6 px-6 md:px-6 pb-2" data-testid="all-leads-table-scroll">
        <div className="min-w-[1300px] lg:min-w-0">
          <Table className="table-fixed lg:text-xs [&_th]:px-2 [&_th]:h-10 [&_td]:px-2 [&_td]:py-2" data-testid="all-leads-table">
            <TableHeader className="sticky top-0 z-10 bg-slate-950/95 backdrop-blur">
              <TableRow className="border-white/5">
                <TableHead className="w-8">
                  <input
                    type="checkbox"
                    data-testid="select-all-checkbox"
                    checked={allSelectedOnPage}
                    onChange={() => toggleAll(visible)}
                    className="accent-amber-500 w-4 h-4 cursor-pointer"
                  />
                </TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="w-[92px]">Phone</TableHead>
                <TableHead className="w-[84px]">Loan</TableHead>
                <TableHead className="w-[84px]">Amount</TableHead>
                <TableHead className="w-[88px]">Source Type</TableHead>
                <TableHead className="w-[84px]">Source Name</TableHead>
                <TableHead className="w-[88px]">Uploaded By</TableHead>
                <TableHead className="w-[96px]">Assigned To</TableHead>
                <TableHead className="w-[104px]">Status</TableHead>
                <TableHead className="w-8"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
          {visible.map(l => {
            const gt = genericType(l);
            const isChecked = selected.has(l.lead_id);
            return (
              <TableRow key={l.lead_id} className="border-white/5" data-testid={`lead-row-${l.lead_id}`}>
                <TableCell className="w-8">
                  <input
                    type="checkbox"
                    data-testid={`row-checkbox-${l.lead_id}`}
                    checked={isChecked}
                    onChange={() => toggleOne(l.lead_id)}
                    className="accent-amber-500 w-4 h-4 cursor-pointer"
                  />
                </TableCell>
                <TableCell className="text-white whitespace-normal break-words leading-tight" title={l.name} data-testid={`lead-name-${l.lead_id}`}>{l.name}</TableCell>
                <TableCell className="font-mono-alt">{l.phone}</TableCell>
                <TableCell className="whitespace-normal break-words leading-tight">{l.loan_type || <span className="text-slate-600">—</span>}</TableCell>
                <TableCell className="whitespace-nowrap">{l.amount ? `₹${parseFloat(l.amount).toLocaleString("en-IN")}` : <span className="text-slate-600">—</span>}</TableCell>
                <TableCell><Badge className={typeColor[gt] + " uppercase text-[9px] tracking-wider whitespace-normal leading-tight px-1.5"} data-testid={`src-type-${l.lead_id}`}>{typeDisplay(l)}</Badge></TableCell>
                <TableCell className="text-slate-300 truncate" title={l.source_name || ""} data-testid={`src-name-${l.lead_id}`}>{l.source_name || <span className="text-slate-600">-</span>}</TableCell>
                <TableCell data-testid={`uploaded-by-${l.lead_id}`}>
                  <div className="text-white truncate" title={uploaderName(l)}>{uploaderName(l)}</div>
                  <div className="text-[9px] text-slate-500 uppercase tracking-wider truncate">{roleLabel(l.source_role)}</div>
                  <div className="text-[9px] text-slate-500 truncate">{fmtDate(l.created_at)}</div>
                </TableCell>
                <TableCell data-testid={`assigned-to-${l.lead_id}`} className="truncate" title={l.assigned_office_staff_name || ""}>
                  {l.assigned_office_staff_name
                    ? <span className="text-white">{l.assigned_office_staff_name}</span>
                    : <span className="text-slate-600">—</span>}
                </TableCell>
                <TableCell><Badge className={statusColor(l.status) + " text-[10px] whitespace-normal break-words leading-tight px-1.5"} title={l.status} data-testid={`status-${l.lead_id}`}>{(l.status || "").replace(/_/g, " ")}</Badge></TableCell>
                <TableCell>
                  <button
                    type="button"
                    onClick={() => openHistory(l.lead_id)}
                    data-testid={`history-btn-${l.lead_id}`}
                    title="View history"
                    className="text-slate-500 hover:text-amber-400 transition-colors"
                  >
                    <Clock className="w-4 h-4"/>
                  </button>
                </TableCell>
              </TableRow>
            );
          })}
          {loaded && filtered.length === 0 && <TableRow><TableCell colSpan={11} className="text-center text-slate-500 py-8">No leads</TableCell></TableRow>}
          {!loaded && loading && <TableRow><TableCell colSpan={11} className="py-2"><LoadingSkeleton rows={6} label="Loading leads…" testid="allleads-loading"/></TableCell></TableRow>}
        </TableBody>
          </Table>
        </div>
      </div>
      {loaded && total > 0 && (
        <Paginator page={page} pageSize={pageSize} total={total} onPage={setPage} testid="allleads-pager"/>
      )}
      <div className="md:hidden text-[10px] text-slate-500 mt-2 text-center">← Swipe horizontally to see all columns →</div>

      {/* Bulk Verify modal */}
      <Dialog open={bulkVerifyOpen} onOpenChange={setBulkVerifyOpen}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-md" data-testid="bulk-verify-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-emerald-300 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4"/> Bulk Verify · {selected.size} lead(s)
            </DialogTitle>
          </DialogHeader>
          <div className="text-xs text-slate-400 mb-3">
            Any field you set gets applied to every selected lead that's still in the raw funnel
            (new number, pending verification, assigned to office). Leaves the rest untouched.
          </div>
          <div className="space-y-3">
            <div>
              <Label className="text-slate-400 text-xs uppercase tracking-widest">Loan Product</Label>
              <select data-testid="bv-loan" value={bvLoan} onChange={e => setBvLoan(e.target.value)}
                className="bg-slate-950 border border-slate-700 text-white rounded-md px-3 h-10 text-sm mt-2 w-full">
                <option value="">— skip —</option>
                {loans.map(l => <option key={l.id} value={l.name}>{l.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-slate-400 text-xs uppercase tracking-widest">District</Label>
              <select data-testid="bv-district" value={bvDistrict} onChange={e => setBvDistrict(e.target.value)}
                className="bg-slate-950 border border-slate-700 text-white rounded-md px-3 h-10 text-sm mt-2 w-full">
                <option value="">— skip —</option>
                {bvDistricts.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-slate-400 text-xs uppercase tracking-widest">Loan Amount (₹) — optional</Label>
              <Input data-testid="bv-amount" type="number" value={bvAmount}
                onChange={e => setBvAmount(e.target.value.replace(/\D/g,""))}
                placeholder="e.g. 500000" className="bg-slate-950 border-slate-700 mt-2"/>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button data-testid="bv-cancel" variant="ghost" onClick={() => setBulkVerifyOpen(false)} className="text-slate-300 hover:text-white">Cancel</Button>
            <Button data-testid="bv-save" onClick={doBulkVerify} disabled={bvSaving || (!bvLoan && !bvDistrict && !bvAmount)}
              className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold rounded-full disabled:opacity-40">
              {bvSaving ? "Verifying…" : `Verify ${selected.size} lead(s)`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reassign confirm dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-lg" data-testid="reassign-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-amber-400">Overwrite existing assignment?</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-300 space-y-3">
            <div>
              <span className="font-bold text-white">{overwriteList.length}</span> of the selected leads {overwriteList.length === 1 ? "is" : "are"} already assigned to another telecaller.
              Reassigning will move {overwriteList.length === 1 ? "it" : "them"} to <span className="text-amber-400 font-bold">{telecallers.find(t => t.id === assignTo)?.name || "the chosen telecaller"}</span>.
            </div>
            <div className="max-h-56 overflow-y-auto rounded-lg border border-white/5 divide-y divide-white/5" data-testid="reassign-list">
              {overwriteList.slice(0, 30).map(c => (
                <div key={c.lead_id} className="px-3 py-2 text-xs flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-mono-alt text-amber-400 truncate">{c.lead_id}</div>
                    <div className="text-slate-400 truncate">{c.name}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Currently</div>
                    <div className="text-slate-300">{c.currentName}</div>
                  </div>
                </div>
              ))}
              {overwriteList.length > 30 && (
                <div className="px-3 py-2 text-[11px] text-slate-500 text-center">…and {overwriteList.length - 30} more</div>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              data-testid="reassign-cancel-btn"
              variant="ghost"
              onClick={() => { setConfirmOpen(false); setOverwriteList([]); }}
              className="text-slate-300 hover:text-white"
            >Cancel</Button>
            <Button
              data-testid="reassign-confirm-btn"
              onClick={executeAssign}
              disabled={assigning}
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full disabled:opacity-40"
            >{assigning ? "Reassigning…" : `Yes, reassign ${overwriteList.length}`}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lead history dialog */}
      <Dialog open={!!historyLead} onOpenChange={(o) => !o && setHistoryLead(null)}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="history-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-amber-400 flex items-center gap-2">
              <HistoryIcon className="w-4 h-4"/> Case Detail — {historyLead?.lead_id}
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm">
            <div className="text-slate-400 mb-3">
              <span className="text-white">{historyLead?.name}</span> · <span className="font-mono-alt">{historyLead?.phone}</span>
            </div>

            {/* Rejection history (if any) — shown first so admin sees blocking issues */}
            {historyLead?.lead?.rejections?.length > 0 && (
              <div className="mb-4"><RejectionHistory lead={historyLead.lead}/></div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              {/* Pipeline timeline (view-only) */}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">9-Stage Pipeline</div>
                <div className="rounded-lg border border-white/5 bg-slate-950/40 p-3">
                  {historyLead?.lead
                    ? <PipelineTimeline lead={historyLead.lead} compact/>
                    : <div className="text-xs text-slate-500 py-4 text-center">Loading…</div>}
                </div>
              </div>
              {/* Activity log */}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Activity Log</div>
                <div className="max-h-96 overflow-y-auto rounded-lg border border-white/5 divide-y divide-white/5" data-testid="history-list">
                  {(historyLead?.history || []).slice().reverse().map((h, idx) => {
                    const isAssign = /assign|round-robin|referral|imported/i.test(h.event || "");
                    const isReject = /REJECTED/i.test(h.event || "");
                    return (
                      <div key={idx} className={`px-3 py-2 flex items-start gap-3 text-xs ${isReject ? "bg-red-500/5" : isAssign ? "bg-amber-500/5" : ""}`} data-testid={`history-row-${idx}`}>
                        <div className="mt-1">
                          <span className={`inline-block w-2 h-2 rounded-full ${isReject ? "bg-red-500" : isAssign ? "bg-amber-400" : "bg-slate-600"}`}/>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={isReject ? "text-red-200" : "text-white"}>{h.event}</div>
                          <div className="text-[10px] text-slate-500 mt-0.5">
                            {(() => { try { return new Date(h.at).toLocaleString("en-IN"); } catch { return h.at; } })()}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {(!historyLead?.history || historyLead.history.length === 0) && (
                    <div className="p-4 text-center text-slate-500 text-xs">No history entries.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end mt-4 gap-2">
            <Button
              data-testid="history-export-csv-btn"
              variant="ghost"
              onClick={() => {
                if (!historyLead?.history?.length) return;
                const esc = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g,'""')}"` : s; };
                const rows = [["Timestamp","Event"]].concat(historyLead.history.map(h => [h.at || "", h.event || ""]));
                const csv = rows.map(r => r.map(esc).join(",")).join("\n");
                const blob = new Blob([csv], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a"); a.href = url; a.download = `history_${historyLead.lead_id}.csv`; a.click();
                URL.revokeObjectURL(url);
                toast.success("CSV downloaded");
              }}
              className="text-amber-400 hover:bg-amber-500/10 border border-amber-500/30 rounded-full"
            >
              <Download className="w-4 h-4 mr-2"/> Export CSV
            </Button>
            <Button data-testid="history-close-btn" onClick={() => setHistoryLead(null)} variant="ghost" className="text-slate-300 hover:text-white">Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk export column picker */}
      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-md" data-testid="export-picker-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-amber-400 flex items-center gap-2">
              <Download className="w-4 h-4"/> Export History · {filtered.length} lead{filtered.length === 1 ? "" : "s"}
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-400 mb-3">Pick the columns you want in the CSV.</div>
          <div className="grid grid-cols-2 gap-2" data-testid="export-cols-list">
            {[
              ["lead_id", "Lead ID"],
              ["name", "Name"],
              ["phone", "Phone"],
              ["loan_type", "Loan Type"],
              ["amount", "Amount"],
              ["status", "Status"],
              ["assigned_office_staff_name", "Assigned To"],
              ["at", "Timestamp"],
              ["event", "Event"],
            ].map(([k, label]) => (
              <label key={k} className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer p-2 rounded hover:bg-white/5">
                <input
                  type="checkbox"
                  checked={!!exportCols[k]}
                  onChange={e => setExportCols(p => ({ ...p, [k]: e.target.checked }))}
                  className="accent-amber-500 w-4 h-4"
                  data-testid={`export-col-${k}`}
                />
                {label}
              </label>
            ))}
          </div>
          <div className="flex justify-between mt-4">
            <div className="flex gap-2">
              <Button
                data-testid="export-select-all"
                variant="ghost"
                className="text-slate-400 hover:text-white text-xs"
                onClick={() => setExportCols({
                  lead_id: true, name: true, phone: true, loan_type: true, amount: true,
                  status: true, assigned_office_staff_name: true, at: true, event: true,
                })}
              >Select all</Button>
              <Button
                data-testid="export-select-min"
                variant="ghost"
                className="text-slate-400 hover:text-white text-xs"
                onClick={() => setExportCols({
                  lead_id: true, name: true, phone: true, loan_type: false, amount: false,
                  status: false, assigned_office_staff_name: false, at: true, event: true,
                })}
              >Minimal</Button>
            </div>
            <div className="flex gap-2">
              <Button data-testid="export-cancel-btn" variant="ghost" onClick={() => setExportOpen(false)} className="text-slate-300 hover:text-white">Cancel</Button>
              <Button data-testid="export-download-btn" onClick={() => exportBulkHistory()} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full">
                <Download className="w-4 h-4 mr-2"/> Download CSV
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -- Payroll --------------------------------------------------
function PayrollPage() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/payroll").then(r => setData(r.data)).catch(e => toast.error(errorMessage(e))); }, []);
  const inr = (n) => `₹${Number(n||0).toLocaleString("en-IN")}`;
  const roleLabel = (r) => ({ telecaller: "Telecaller", field: "Field", district_manager: "District Manager" }[r] || r);

  const downloadExcel = async () => {
    try {
      const res = await api.get("/admin/payroll/export.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `payroll_${data?.month || ""}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const downloadCsv = () => {
    if (!data?.staff?.length) return toast.error("No staff to export");
    const esc = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g,'""')}"` : s; };
    const header = ["Name","Phone","Role","District","Monthly Salary","Status"];
    const lines = [header.join(",")].concat(data.staff.map(u => [
      u.name, u.phone, roleLabel(u.sub_role), u.district || u.managed_district || "", u.monthly_salary || 0, u.status || "active"
    ].map(esc).join(",")));
    lines.push([`TOTAL`, "", "", "", data.total_payroll, ""].map(esc).join(","));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `payroll_${data.month}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="payroll-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="font-heading text-xl text-white">Monthly Payroll · {data?.month || ""}</div>
          <div className="text-sm text-slate-500">All office staff. Grouped by district and role.</div>
        </div>
        <div className="flex gap-2">
          <Button onClick={downloadCsv} data-testid="payroll-csv-btn" variant="secondary" className="bg-slate-800 hover:bg-slate-700 text-white rounded-full"><Download className="w-4 h-4 mr-2"/>CSV</Button>
          <Button onClick={downloadExcel} data-testid="payroll-xlsx-btn" className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full"><Download className="w-4 h-4 mr-2"/>Excel</Button>
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        <div className="card-surface p-6" data-testid="payroll-total"><Wallet className="w-5 h-5 text-amber-500 mb-3"/><div className="font-display text-3xl text-white">{inr(data?.total_payroll)}</div><div className="text-xs uppercase tracking-widest text-slate-500 mt-2">Total Monthly Payout</div></div>
        <div className="card-surface p-6"><Users2 className="w-5 h-5 text-amber-500 mb-3"/><div className="font-display text-3xl text-white">{data?.headcount ?? 0}</div><div className="text-xs uppercase tracking-widest text-slate-500 mt-2">Headcount</div></div>
        <div className="card-surface p-6"><BarChart3 className="w-5 h-5 text-amber-500 mb-3"/><div className="font-display text-3xl text-white">{data?.by_district?.length ?? 0}</div><div className="text-xs uppercase tracking-widest text-slate-500 mt-2">Districts</div></div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="card-surface p-6">
          <div className="font-heading text-white mb-4">By District</div>
          <Table><TableHeader><TableRow className="border-white/5"><TableHead>District</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
            <TableBody>{(data?.by_district || []).map(r => (
              <TableRow key={r.district} className="border-white/5"><TableCell className="text-white">{r.district}</TableCell><TableCell className="text-right font-mono-alt text-amber-400">{inr(r.total)}</TableCell></TableRow>
            ))}</TableBody>
          </Table>
        </div>
        <div className="card-surface p-6">
          <div className="font-heading text-white mb-4">By Role</div>
          <Table><TableHeader><TableRow className="border-white/5"><TableHead>Role</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
            <TableBody>{(data?.by_role || []).map(r => (
              <TableRow key={r.role} className="border-white/5"><TableCell className="text-white">{roleLabel(r.role)}</TableCell><TableCell className="text-right font-mono-alt text-amber-400">{inr(r.total)}</TableCell></TableRow>
            ))}</TableBody>
          </Table>
        </div>
      </div>

      <div className="card-surface p-6">
        <div className="font-heading text-white mb-4">All Staff</div>
        <Table>
          <TableHeader><TableRow className="border-white/5"><TableHead>Name</TableHead><TableHead>Phone</TableHead><TableHead>Role</TableHead><TableHead>District</TableHead><TableHead className="text-right">Salary</TableHead></TableRow></TableHeader>
          <TableBody>
            {(data?.staff || []).map(u => (
              <TableRow key={u.id} className="border-white/5" data-testid={`payroll-row-${u.id}`}>
                <TableCell className="text-white">{u.name}</TableCell>
                <TableCell className="font-mono-alt">{u.phone}</TableCell>
                <TableCell>{roleLabel(u.sub_role)}</TableCell>
                <TableCell>{u.district || u.managed_district || <span className="text-slate-600">—</span>}</TableCell>
                <TableCell className="text-right font-mono-alt text-amber-400">{inr(u.monthly_salary)}</TableCell>
              </TableRow>
            ))}
            {(!data || data.staff.length === 0) && <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">No office staff</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// -- Refunds --------------------------------------------------
function RefundsPage() {
  const [rows, setRows] = useState([]);
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const load = () => api.get("/admin/refunds").then(r => setRows(r.data)).catch(e => toast.error(errorMessage(e)));
  useEffect(() => { load(); }, []);
  const inr = (n) => `₹${Number(n||0).toLocaleString("en-IN")}`;
  const approve = async (id) => { try { await api.post(`/admin/refunds/${id}/approve`); toast.success("Refund approved and paid"); load(); } catch(e){toast.error(errorMessage(e));} };
  const doReject = async () => {
    try {
      await api.post(`/admin/refunds/${rejectId}/reject`, { reject_reason: rejectReason });
      toast.success("Refund rejected"); setRejectId(null); setRejectReason(""); load();
    } catch(e){toast.error(errorMessage(e));}
  };
  const color = (s) => ({ requested:"bg-amber-500/20 text-amber-400 border-amber-500/30", refunded:"bg-emerald-500/20 text-emerald-400 border-emerald-500/30", rejected:"bg-red-500/20 text-red-400 border-red-500/30" }[s] || "bg-slate-700");
  return (
    <div className="card-surface p-6" data-testid="refunds-page">
      <div className="mb-6">
        <div className="font-heading text-xl text-white">Refund Requests</div>
        <div className="text-sm text-slate-500">Refund = fees paid minus GST (18% GST portion is kept by the government).</div>
      </div>
      <Table>
        <TableHeader><TableRow className="border-white/5"><TableHead>Requested</TableHead><TableHead>Lead</TableHead><TableHead>Customer</TableHead><TableHead>Fees Paid</TableHead><TableHead>GST Kept</TableHead><TableHead>Refund</TableHead><TableHead>Reason</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>
          {rows.map(r => (
            <TableRow key={r.id} className="border-white/5" data-testid={`refund-row-${r.id}`}>
              <TableCell className="text-slate-400 text-xs font-mono-alt whitespace-nowrap">{new Date(r.created_at).toLocaleDateString("en-IN")}</TableCell>
              <TableCell className="font-mono-alt text-amber-400">{r.lead_id}</TableCell>
              <TableCell><div className="text-white">{r.customer_name}</div><div className="text-xs text-slate-500 font-mono-alt">{r.customer_phone}</div></TableCell>
              <TableCell className="font-mono-alt text-slate-300">{inr(r.fees_paid)}</TableCell>
              <TableCell className="font-mono-alt text-slate-500">{inr(r.gst_amount)}</TableCell>
              <TableCell className="font-mono-alt text-emerald-400 font-bold">{inr(r.refund_amount)}</TableCell>
              <TableCell className="text-slate-400 max-w-xs text-sm">{r.reason}</TableCell>
              <TableCell><Badge className={color(r.status)}>{r.status}</Badge>{r.reject_reason && <div className="text-xs text-red-400 mt-1">{r.reject_reason}</div>}</TableCell>
              <TableCell>
                {r.status === "requested" && (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => approve(r.id)} data-testid={`refund-approve-${r.id}`} className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 rounded-full"><CheckCircle2 className="w-3 h-3 mr-1"/>Approve</Button>
                    <Button size="sm" variant="secondary" onClick={() => setRejectId(r.id)} data-testid={`refund-reject-${r.id}`} className="bg-slate-800 hover:bg-slate-700 text-white rounded-full"><XCircle className="w-3 h-3 mr-1"/>Reject</Button>
                  </div>
                )}
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-8">No refund requests yet</TableCell></TableRow>}
        </TableBody>
      </Table>
      <Dialog open={!!rejectId} onOpenChange={(v) => !v && setRejectId(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white">
          <DialogHeader><DialogTitle>Reject Refund</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label className="text-slate-400 text-xs uppercase tracking-widest">Reason</Label>
            <Textarea data-testid="refund-reject-reason" value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Why are you rejecting this refund?" className="bg-slate-950 border-slate-700"/>
            <Button data-testid="refund-reject-confirm" onClick={doReject} disabled={!rejectReason.trim()} className="w-full bg-red-500 hover:bg-red-400 text-white font-bold rounded-full">Confirm Reject</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -- Settings + Payout are extracted to /pages/admin/*.jsx --

export default function AdminDashboard() {
  const [active, setActive] = useState("overview");
  const [urlEditUser, setUrlEditUser] = useState(null);
  const [urlEditLocs, setUrlEditLocs] = useState([]);
  const [urlEditBanks, setUrlEditBanks] = useState([]);
  // If the impersonation banner sent us here via ?edit_user=<id>, auto-open modal
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const uid = params.get("edit_user");
    if (!uid) return;
    (async () => {
      try {
        const [u, ll, bb] = await Promise.all([
          api.get(`/admin/users/${uid}`),
          api.get("/districts").catch(() => ({ data: [] })),
          api.get("/banks").catch(() => ({ data: [] })),
        ]);
        setUrlEditLocs(ll.data || []);
        setUrlEditBanks(bb.data || []);
        setUrlEditUser(u.data);
        if (u.data?.role === "bank_staff") setActive("bank_staff");
        else if (u.data?.role === "office_staff") setActive("office_staff");
      } catch (e) { toast.error(errorMessage(e)); }
      try { window.history.replaceState({}, "", "/admin"); } catch { /* ignore */ }
    })();
  }, []);
  return (
    <DashLayout title="Admin" subtitle="Control Center" testId="admin" nav={NAV} activeKey={active} onNav={setActive}>
      {active === "overview" && <Overview/>}
      {active === "quick_add" && <UniversalLeadAdd/>}
      {active === "source_report" && <SourceReportPage/>}
      {active === "loans" && <LoansPage/>}
      {active === "banks" && <BanksPage/>}
      {active === "bank_staff" && <BankStaffPage/>}
      {active === "office_staff" && <OfficeStaffPage/>}
      {active === "partners" && <AdminPartnersPage/>}
      {active === "master_map" && <AdminMasterMap/>}
      {active === "call_logs" && <CallLogsPage/>}
      {active === "upload" && <UploadLeadsPage/>}
      {active === "sources_pool" && <SourcesPoolPage/>}
      {active === "all_leads" && <AllLeadsPage/>}
      {active === "in_bank_pool" && <InBankPoolPage/>}
      {active === "moving_files" && <AdminMovingFiles/>}
      {active === "sla" && <SLAMonitorPage/>}
      {active === "commission" && <CommissionReportPage/>}
      {active === "payroll" && <PayrollPage/>}
      {active === "payout" && <PayoutPage/>}
      {active === "refunds" && <RefundsPage/>}
      {active === "audit" && <AuditLogPage/>}
      {active === "impersonation_logs" && <ImpersonationLogsPage/>}
      {active === "settings" && <SettingsPage/>}
      {active === "location" && <LocationMasterPage/>}
      <AdminEditUserModal
        staff={urlEditUser}
        banks={urlEditBanks}
        districts={urlEditLocs}
        open={!!urlEditUser}
        onClose={() => setUrlEditUser(null)}
        onSaved={() => setUrlEditUser(null)}
      />
    </DashLayout>
  );
}
